"use client";

import { useState, useCallback, useEffect, useRef } from "react";
import { Toolbar } from "../../components/Toolbar";
import * as fabric from "fabric";
import utils from "../../../utils/utils";

import * as htmlToImage from "html-to-image";
import { useScreenshot, createFileName } from "use-react-screenshot";

import { BottomToolbar } from "../../components/BottomToolbar";
import { StylePanel } from "../../components/StylePanel";
import { RotationHandle } from "../../components/RotationHandle";
import { CommentInput } from "../../components/CommentInput";
import { CommentTooltip } from "../../components/CommentTooltip";
import { CommentThread } from "../../components/CommentThread";
import { MeasurementPanel } from "../../components/MeasurementPanel";
import { CommentPanel } from "../../components/CommentPanel";
import MeasurementCanvas from "../../components/MeasurementCanvas";

import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { distUnits } from "../../helpers/interactions/context";
import {
  calculatePolygonArea,
  calculatePolygonPerimeter,
} from "../../helpers/geometry";
import { dentalIcons } from "../../../dentail-indetifcation/utils-icons/dentalIcons";
import CustomDefaultButton from "../../../dentail-indetifcation/components/d-reusable/CustomDefaultButton";
import CustomButtonWithIcon from "../../../dentail-indetifcation/components/d-reusable/CustomButtonWithIcon";
import CustomPrimaryButton from "../../../dentail-indetifcation/components/d-reusable/CustomPrimaryButton";
import Calculator from "../../components/ui/Calculator";
import { message, Tooltip } from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { _onSaveProjectSnapshot } from "../../../redux/actions/imaga-analysis/projectActions";
import {
  dataURLToBlob,
  fetchAnalysisDoc,
  normalizePolygonMinimal,
} from "../../helpers/normalize/normalize";
import ZoomOnly from "../../components/ZoomOnly";
import RotationWarning from "../../components/RotationWarning";
import ResetWarning from "../../components/ResetWarning";
import { Slider } from "../../components/ui/slider";
import {
  addJsonToAnalysisProject,
  addThumbnailToProject,
  getAnalysisProjectDetailsById,
} from "../../../components/https/image-analysis-apis";
import { errorRequestHandel } from "../../../helper";
import HomePageShimer from "../../components/shimer/HomePageShimer";
import { saveAs } from "file-saver";
import CropWarning from "../../components/CropWarning";
import useCommentsCollaboration from "../../hooks/useCommentsCollaboration";
import useCollabMeasurements from "../../hooks/useCollabMeasurements";
import useCollabSavedMeasurements from "../../hooks/useCollabSavedMeasurements";
import useCollabScale from "../../hooks/useCollabScale";
import useCollabGuidelines from "../../hooks/useCollabGuidelines";
import useCollabRotation from "../../hooks/useCollabRotation";
import useCollabImageSrc from "../../hooks/useCollabImageSrc";
import { setHighlightedPolygonId } from "../../helpers/renderFabric";
import { Folder } from "lucide-react";
import FlipImageWarming from "../../components/FlipWarning";
import useCollabDots from "../../hooks/useCollabDots";
import useCollabArrows from "../../hooks/useCollabArrows";
import { useSubscriptionGuard } from "../../../hooks/subscription-hook/UseSubscriptionGuard";

const getCroppedImage = (image, crop) => {
  const canvas = document.createElement("canvas");
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  canvas.width = crop.width * scaleX;
  canvas.height = crop.height * scaleY;

  const ctx = canvas.getContext("2d");
  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width * scaleX,
    crop.height * scaleY
  );

  return canvas.toDataURL();
};

const DentalMeasurementTool = () => {
  const navigate = useNavigate();
  useSubscriptionGuard();

  const canvasRef = useRef(null);
  const imgRef = useRef(null);
  const measurementCanvasRef = useRef(null);
  const bgLoadedForProjectRef = useRef(null);
  const hydratedForProjectRef = useRef(null);
  // keep the very first uploaded dataURL, so we don't rotate an already-rotated PNG
  const originalImageSrcRef = useRef(null);
  // keep cumulative angle we want to show
  const cumulativeAngleRef = useRef(0);
  const analysisDocRef = useRef(null);
  const previewAngleRef = useRef(0);
  const previewBakedRef = useRef(0);
  const screenShotRef = useRef(null);
  const thumbnailReff = useRef(null);
  const levelsApplyRAF = useRef(0);
  const levelsPending = useRef(null);
  const pendingAngleRef = useRef(null);
  // at top-level
  const rotateDebounceRef = useRef(null);
  const ROTATE_INPUT_DELAY = 500; // ms (use 2000 for 2s)

  //scrub refs
  const isScrubbingRef = useRef(false);
  const startXRef = useRef(0);
  const baseAngleRef = useRef(0);

  const PX_PER_DEG = 12; // feel
  const STEP_DEG = 1;

  /** Instant, center-anchored zoom for the Scale tool slider (100–200%). */
  const zoomShellRef = useRef(null);
  let _zoomSliderRaf = 0;
  let _zoomSliderPending = null;

  const { id: projectId } = useParams(); // 👈 Extracts "id" from the route

  const allProjects = useSelector((state) => state.analysis.projects || []);
  // const savedProject = allProjects.find((p) => p.id === projectId);
  const dispatch = useDispatch();
  const [activeTool, setActiveTool] = useState("select");

  const [image, setImage] = useState(null);
  const [scale, setScale] = useState("");
  const [scaleLength, setScaleLength] = useState(100);
  const [unit, setUnit] = useState("px");
  const [measurements, setMeasurements] = useState([]);
  const [savedMeasurements, setSavedMeasurements] = useState([]);
  const [canvasSize, setCanvasSize] = useState({ width: 600, height: 600 });
  const [displaySize, setDisplaySize] = useState({ width: 600, height: 600 });
  const [imageTransforms, setImageTransforms] = useState({
    contrast: 1,
    rotation: 0,
    position: { x: 0, y: 0 },
    inverted: false,
    sharpened: false,
  });
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [screenshotRequested, setScreenshotRequested] = useState(false);
  const [zoom, setZoom] = useState(1);
  // New state for advanced features
  const [comments, setComments] = useState([]);
  const [selectedCommentId, setSelectedCommentId] = useState();
  const [showCommentPanel, setShowCommentPanel] = useState(false);

  const [wrapperRotation, setWrapperRotation] = useState(0);

  const [styleSettings, setStyleSettings] = useState({
    linear: {
      color: "#00FF00",
      thickness: 2,
      dashStyle: "dotted",
    },
    angle: {
      color: "#FFFFFF",
      thickness: 2,
      dashStyle: "dotted",
    },
    polygon: {
      color: "#FFD700",
      thickness: 2,
      dashStyle: "dotted",
    },
    unitColor: "#0D99FF", // stays global
    importStrokeColor: "#10B981",
    importBgColor: "#12B9814D", //first priority
    // importBgColor: "#12B98166",//2nd priority
    importCircleColor: "#10B981",
    highlightedColor: "#FF14934D",
    type: "linear", // current active type
  });

  const [showStylePanel, setShowStylePanel] = useState(false);
  const [stylePanelPosition, setStylePanelPosition] = useState({ x: 0, y: 0 });

  const [isPanMode, setIsPanMode] = useState(false);

  // New state for comment UI management
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [commentInputPosition, setCommentInputPosition] = useState({
    x: 0,
    y: 0,
  });
  const [showCommentTooltip, setShowCommentTooltip] = useState(false);
  const [tooltipComment, setTooltipComment] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showCommentThread, setShowCommentThread] = useState(false);
  const [threadComment, setThreadComment] = useState(null);
  const [threadPosition, setThreadPosition] = useState({ x: 0, y: 0 });
  const [selectedToolId, setSelectedToolId] = useState("");
  const [showCalculator, setShowCalculator] = useState(false);

  // --- State with ranges & current values
  const [imageControls, setImageControls] = useState({
    contrast: { min: -1, max: 1, step: 0.05, neutral: 0, value: 0 },
    brightness: { min: -1, max: 1, step: 0.05, neutral: 0, value: 0 },
    invert: { min: 0, max: 1, step: 0.01, neutral: 0, value: 0 }, // use as boolean toggle (0/1)
    sharpness: { min: 0, max: 3, step: 0.05, neutral: 0, value: 0 },
    zoom: { min: 0.25, max: 4, step: 0.05, neutral: 1, value: 1 },
  });

  const [sharpenAmount, setSharpenAmount] = useState(0);
  const [sharpenRadius, setSharpenRadius] = useState(0);
  const [sharpenNoise, setSharpenNoise] = useState(0);

  const [imageSrc, setImageSrc] = useState(null);
  const [isCropping, setIsCropping] = useState(false);
  const [crop, setCrop] = useState({
    unit: "%", // or "px"
    x: 10,
    y: 10,
    width: 50, // 50% of the image width
    height: 50,
  });
  const [completedCrop, setCompletedCrop] = useState(null);

  const [dragging, setDragging] = useState(false);
  const [selectedObjectId, setSelectedObjectId] = useState(null);

  const [levelsAdjustments, setLevelsAdjustments] = useState({
    black: 0,
    mid: 50,
    white: 100,
  });
  const [levelsHistogram, setLevelsHistogram] = useState(
    new Array(256).fill(0)
  );
  const [flipWarning, setFlipWarning] = useState(false);
  const [rotationWarning, setRotationWarning] = useState(false);
  const [resetWarning, setResetWarning] = useState(false);
  const [cropWarning, setCropWarning] = useState(false);
  const [isDataLoading, setisDataLoading] = useState(true);
  const [savedProject, setSavedProject] = useState(null);
  const [resetImportData, setResetImportData] = useState(false);
  // at top-level inside the component
  const [selectedColor, setSelectedColor] = useState(null);
  const [selectedLineStyle, setSelectedLineStyle] = useState(null);
  const [selectedLineThikness, setSelectedLineThinkness] = useState(2);

  const [selectedTab, setSelectedTab] = useState("");

  const [curves, setCurves] = useState({
    shadows: 0,
    midtones: 0,
    highlights: 0,
  });
  const curvesRef = useRef({ shadows: 0, midtones: 0, highlights: 0 });

  const [imageBase64StringObj, setImageBase64StringObj] = useState({
    fileName: "",
    width: "",
    height: "",
    data: "",
  });

  const userdata = useSelector(
    (state) => state.settings_data.profileSettings_data
  );

  // hook usage
  const {
    addComment,
    updateComment,
    deleteComment: deleteCommentRemote,
    addReply: addReplyRemote,
    updateReply: updateReplyRemote,
    resolveComment: resolveCommentRemote,
  } = useCommentsCollaboration(projectId, setComments);

  const { syncMeasurements } = useCollabMeasurements(projectId, (remote) => {
    const api = measurementCanvasRef.current;
    if (!api) return;

    const incoming = Array.isArray(remote) ? remote : [];
    const byId = new Map(incoming.map((it) => [it.id, it]));
    const isPolygon = (it) => Array.isArray(it?.points);

    // 1) read what child currently renders
    const current = api.getMergedMeasurements?.() || [];
    const currentPolys = current.filter(isPolygon);
    const currentActive = current.filter((it) => !isPolygon(it));

    // 2) drop actives that were deleted remotely, update survivors, add new
    const nextActive = currentActive
      .filter((it) => byId.has(it.id)) // ← remove deleted
      .map((it) => ({ ...it, ...byId.get(it.id) })); // ← update kept ones

    for (const it of incoming) {
      if (!isPolygon(it) && !nextActive.some((x) => x.id === it.id)) {
        nextActive.push(it); // ← add new remote items
      }
    }

    // 3) re-load child with reconciled actives + preserved polygons
    api.loadFromMergedMeasurements?.([...nextActive, ...currentPolys]);

    // 4) mirror to your UI list
    setMeasurements(incoming || []);
  });

  const { syncSavedMeasurements } = useCollabSavedMeasurements(
    projectId,
    (remoteSaved) => setSavedMeasurements(remoteSaved || [])
  );

  const { syncScale } = useCollabScale(projectId, (remote) => {
    if (remote.unit !== undefined) setUnit(remote.unit);
    if (remote.scale !== undefined) setScale(remote.scale);
    if (remote.scaleLength !== undefined) setScaleLength(remote.scaleLength);
    // push the overlay down to child
    measurementCanvasRef.current?.setScaleOverlay?.({
      position:
        remote.scalePosX !== undefined && remote.scalePosY !== undefined
          ? { x: remote.scalePosX, y: remote.scalePosY }
          : undefined,
      length: remote.scaleLength,
      angle: remote.scaleAngle,
    });
  });

  // --- GUIDELINES SYNC (parent watches child, pushes on change) ---

  const lastGuidelinesJsonRef = useRef("");
  const echoGuardRef = useRef(false);

  const { syncGuidelines } = useCollabGuidelines(projectId, (remote) => {
    const safe = Array.isArray(remote) ? remote : [];

    echoGuardRef.current = true;
    measurementCanvasRef.current?.loadGuidelines?.(safe);

    // IMPORTANT: snapshot after accepting remote so the poller won’t resend
    lastGuidelinesJsonRef.current = JSON.stringify(safe);

    queueMicrotask?.(() => {
      echoGuardRef.current = false;
    });
  });

  const broadcastGuidelines = () => {
    if (echoGuardRef.current) return;
    const arr = measurementCanvasRef.current?.getGuidelines?.() || [];
    const json = JSON.stringify(arr);
    if (json !== lastGuidelinesJsonRef.current) {
      syncGuidelines(arr);
      lastGuidelinesJsonRef.current = json; // snapshot after local send
    }
  };

  useEffect(() => {
    const id = setInterval(broadcastGuidelines, 250);
    return () => clearInterval(id);
  }, []);

  // --- dots SYNC (parent watches child, pushes on change) ---
  const lastDotJsonRef = useRef("");
  const echoGuardDotRef = useRef(false);

  const { syncDots } = useCollabDots(projectId, (remote) => {
    const safe = Array.isArray(remote) ? remote : [];

    echoGuardDotRef.current = true;
    measurementCanvasRef.current?.loadDots?.(safe);

    // IMPORTANT: snapshot after accepting remote so the poller won’t resend
    lastDotJsonRef.current = JSON.stringify(safe);

    queueMicrotask?.(() => {
      echoGuardDotRef.current = false;
    });
  });

  const broadcastDots = () => {
    if (echoGuardDotRef.current) return;
    const arr = measurementCanvasRef.current?.getDots?.() || [];
    const json = JSON.stringify(arr);
    if (json !== lastDotJsonRef.current) {
      syncDots(arr);
      lastDotJsonRef.current = json; // snapshot after local send
    }
  };

  useEffect(() => {
    const id = setInterval(broadcastDots, 250);
    return () => clearInterval(id);
  }, []);

  // --- arrows SYNC (parent watches child, pushes on change) ---
  const lastArrowJsonRef = useRef("");
  const echoGuardArrowRef = useRef(false);

  const { syncArrows } = useCollabArrows(projectId, (remote) => {
    const safe = Array.isArray(remote) ? remote : [];

    echoGuardArrowRef.current = true;
    measurementCanvasRef.current?.loadArrows?.(safe);

    // IMPORTANT: snapshot after accepting remote so the poller won’t resend
    lastArrowJsonRef.current = JSON.stringify(safe);

    queueMicrotask?.(() => {
      echoGuardArrowRef.current = false;
    });
  });

  const broadcastArrows = () => {
    if (echoGuardArrowRef.current) return;
    const arr = measurementCanvasRef.current?.getArrows?.() || [];
    const json = JSON.stringify(arr);
    if (json !== lastArrowJsonRef.current) {
      syncArrows(arr);
      lastArrowJsonRef.current = json; // snapshot after local send
    }
  };

  useEffect(() => {
    const id = setInterval(broadcastArrows, 250);
    return () => clearInterval(id);
  }, []);

  // when child reports local ruler changes, broadcast partials
  const onScaleOverlayChange = ({ position, length, angle }) => {
    if (position) syncScale({ scalePosX: position.x, scalePosY: position.y });
    if (typeof length === "number") {
      setScaleLength(length);
      syncScale({ scaleLength: length });
    }
    if (typeof angle === "number") syncScale({ scaleAngle: angle });
  };

  const rotationEchoGuardRef = useRef(false); // avoid echo loops
  const lastSentRotationRef = useRef(null);
  const norm360 = (a) => ((Number(a) % 360) + 360) % 360; // 0..359

  const { syncRotation } = useCollabRotation(projectId, (remoteAngle) => {
    // Ignore if we're applying a remote already
    if (rotationEchoGuardRef.current) return;

    const incoming = norm360(remoteAngle);
    const current = norm360(cumulativeAngleRef.current || 0);

    // If angle is the same, no-op
    if (incoming === current) return;

    // If user is mid-scrub or input-debounced, defer until they finish
    if (isScrubbingRef.current || rotateDebounceRef.current) {
      pendingAngleRef.current = incoming;
      return;
    }

    // Apply remote angle (absolute) exactly once, don't broadcast back
    rotationEchoGuardRef.current = true;
    onInitTransFormHandel(incoming);
    // release guard next tick to allow our next local change to broadcast
    queueMicrotask?.(() => {
      rotationEchoGuardRef.current = false;
    });
  });

  const broadcastRotationIfNeeded = (angleAbs) => {
    if (rotationEchoGuardRef.current) return; // we are applying a remote change
    const a = norm360(angleAbs);
    if (a === lastSentRotationRef.current) return; // avoid duplicate sends
    syncRotation(a);
    lastSentRotationRef.current = a;
  };

  const imageEchoGuardRef = useRef(false);
  const { syncImageSrc } = useCollabImageSrc(projectId, (remoteDataUrl) => {
    if (imageEchoGuardRef.current) return; // prevent loops
    imageEchoGuardRef.current = true;

    // IMPORTANT: remote needs the same path your local flows use:
    // 1) load into canvas
    handleImageUploadToCanvas(remoteDataUrl);

    // 2) update state/refs your code relies on later
    setImageSrc(remoteDataUrl);
    originalImageSrcRef.current = remoteDataUrl;
    setImageBase64StringObj((prev) => ({ ...prev, data: remoteDataUrl }));

    queueMicrotask?.(() => {
      imageEchoGuardRef.current = false;
    });
  });

  // small helpers
  const normalizeAngle = (a) => {
    const n = ((Number(a) % 360) + 360) % 360; // 0..359
    return n > 180 ? n - 360 : n; // −180..+180
  };
  const clamp = (x, min, max) => Math.min(max, Math.max(min, x));
  const getBG = () => {
    const canvas = canvasRef.current;
    if (!canvas) return {};
    const bg = canvas.backgroundImage;
    return { canvas, bg };
  };
  const takeScreenShot = async (node) => {
    const dataURI = await htmlToImage.toPng(node, {
      backgroundColor: "white",
      quality: 1,
      // pixelRatio: 1,
    });
    return dataURI;
  };
  const download = (
    image,
    { name = "screen shot", extension = "jpeg" } = {}
  ) => {
    const a = document.createElement("a");
    a.href = image;
    a.download = createFileName(extension, name);
    a.click();
    message.success("Screenshot has been downloaded");
  };
  const downloadScreenshot = () =>
    takeScreenShot(screenShotRef.current).then(download);

  const takeThumbnailScreenshot = () => takeScreenShot(thumbnailReff.current);

  // map buttons -> degrees rotations
  const ROTATION_BY_TOOL = {
    "rotate-left-1": -1,
    "rotate-right-1": 1,
    "rotate-left-90": -90,
    "rotate-right-90": 90,
  };

  useEffect(() => {
    getProjectDetails();
  }, []);

  const getProjectDetails = async () => {
    try {
      setisDataLoading(true);
      const response = await getAnalysisProjectDetailsById({ id: projectId });
      if (response.status === 200) {
        setSavedProject(response.data.data);
        await loadBackgroundImageWhenProjectLoad(response.data.data);
        setHighlightedPolygonId(null);
        setisDataLoading(false);
      }
    } catch (error) {
      errorRequestHandel({ error: error });
      setisDataLoading(false);
    }
  };

  // upload background image
  const loadBackgroundImageWhenProjectLoad = async (savedProject) => {
    // avoid duplicate loads for same project
    if (bgLoadedForProjectRef.current === projectId) return;

    const url = savedProject?.analysis_data; // e.g. https://.../image_analysis/3.json
    if (!url) return;
    setisDataLoading(true);

    try {
      const doc = await fetchAnalysisDoc(url);
      if (!doc) return;

      analysisDocRef.current = doc;

      // background image URL from the JSON
      const savedBgUrl = doc?.background?.imageUrl || doc?.background?.imageSrc;
      if (!savedBgUrl || image) return;

      bgLoadedForProjectRef.current = projectId;

      // your existing uploader (string URL is fine)
      handleImageUploadToCanvas(savedBgUrl);
      setImageSrc(savedBgUrl);
      originalImageSrcRef.current = savedBgUrl;

      setisDataLoading(false);
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("load analysis_data (bg) failed:", err);
      }
      setisDataLoading(false);
    }
  };

  // Hydrate overlays AFTER the image is present
  useEffect(() => {
    if (!image) return; // wait until background is on canvas
    if (hydratedForProjectRef.current === projectId) return;

    const doc = analysisDocRef.current;
    if (!doc) return;

    hydratedForProjectRef.current = projectId;

    try {
      const background = doc?.background;
      const overlays = doc?.overlays || {};
      const unit = overlays.unit ?? "px";
      const scaleVal = overlays.scale ?? "";
      const scaleLengthVal = overlays.scaleLength ?? 100;
      const commentsVal = overlays.comments ?? [];
      const meas = overlays.measurements ?? [];
      const textAnn = overlays.textAnnotations ?? [];
      const rotationAngle = Number(overlays.rotationAngle ?? 0);
      const savedMeasurement = overlays?.savedMeasurements || [];
      const guidelines = overlays?.guidelines || [];
      const dots = overlays.dots ?? [];
      const arrows = overlays.arrows ?? [];
      if (Array.isArray(guidelines) && guidelines.length > 0) {
        measurementCanvasRef.current?.loadGuidelines?.(guidelines);
      }
      if (Array.isArray(meas) && meas.length > 0) {
        measurementCanvasRef.current?.loadFromMergedMeasurements?.(meas);
        setTimeout(() => setMeasurements(meas), 100); // delay helps Fabric finish

        // setMeasurements(meas);
      } else {
        setMeasurements([]);
      }

      if (Array.isArray(textAnn)) {
        measurementCanvasRef.current?.loadTextAnnotations?.(textAnn);
      }
      // dots
      if (Array.isArray(dots)) {
        measurementCanvasRef.current?.loadDots?.(dots);
      }
      // arrows
      if (Array.isArray(arrows)) {
        measurementCanvasRef.current?.loadArrows?.(arrows);
      }

      setUnit(unit);
      setScale(scaleVal);
      setScaleLength(scaleLengthVal);
      setComments(Array.isArray(commentsVal) ? commentsVal : []);
      setSavedMeasurements(savedMeasurement);
      setImageBase64StringObj(background?.baseImageData);
      // absolute rotation (from original)
      cumulativeAngleRef.current = rotationAngle;
      setWrapperRotation(rotationAngle);
      if (rotationAngle !== 0) {
        // rotate & re-upload from ORIGINAL just once
        onInitTransFormHandel(rotationAngle);
      }
    } catch (err) {
      console.error("hydrate overlays failed:", err);
    }
  }, [image, projectId, savedProject]);

  const onInitTransFormHandel = async (absoluteAngle) => {
    setRotationWarning(false);
    const target = normalizeAngle(absoluteAngle ?? wrapperRotation ?? 0);

    // set ABSOLUTE angle; always rotate FROM original
    cumulativeAngleRef.current = target;
    const rotatedDataUrl =
      target === 0
        ? originalImageSrcRef.current
        : await rotateDataURL(originalImageSrcRef.current, target);

    handleImageUploadToCanvas(rotatedDataUrl);
    setImageSrc(rotatedDataUrl);
    setWrapperRotation(target);
    pendingAngleRef.current = null;
  };

  //image uploading and croping section
  const handleSkipCrop = () => {
    handleImageUploadToCanvas(imageSrc);
    setIsCropping(false);
    setImageSrc(imageSrc);
    originalImageSrcRef.current = imageSrc;
    if (!imageEchoGuardRef.current) syncImageSrc(imageSrc);
    setCrop({
      unit: "%", // or "px"
      x: 10,
      y: 10,
      width: 50, // 50% of the image width
      height: 50,
    });
  };
  const handleImageUploadToCanvas = (fileOrDataUrl) => {
    if (typeof fileOrDataUrl === "string") {
      // fileOrDataUrl is a DataURL string
      const img = new Image();
      img.onload = () => {
        setImage(img);
        setImageBase64StringObj((prev) => ({
          ...prev,
          height: img.height,
          width: img.width,
        }));

        // Reset transforms when new image is loaded
        setImageTransforms({
          contrast: 1,
          rotation: 0,
          position: { x: 0, y: 0 },
          inverted: false,
          sharpened: false,
        });
        // Clear measurements when new image is loaded
        setMeasurements([]);
        setComments([]);
        setZoom(1);
      };
      img.src = fileOrDataUrl;
    } else {
      // fileOrDataUrl is a Blob/File
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          setImage(img);
          setImageBase64StringObj((prev) => ({
            ...prev,
            height: img.height,
            width: img.width,
          }));
          // Reset transforms when new image is loaded
          setImageTransforms({
            contrast: 1,
            rotation: 0,
            position: { x: 0, y: 0 },
            inverted: false,
            sharpened: false,
          });
          // Clear measurements when new image is loaded
          setMeasurements([]);
          setComments([]);
          setZoom(1);
        };
        img.src = e.target?.result;
      };
      reader.readAsDataURL(fileOrDataUrl);
    }
  };
  const handleImageUpload = (file) => {
    handleReset();
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setImageSrc(event.target.result);
        originalImageSrcRef.current = event.target.result;

        setImageBase64StringObj((prev) => ({
          ...prev,
          data: event.target.result,
          fileName: file.name,
        }));
        // setIsCropping(true);
        handleImageUploadToCanvas(event.target.result);
        if (!imageEchoGuardRef.current) syncImageSrc(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleCropImage = () => {
    if (!imageSrc) return;

    const drawingsExist = (measurements?.length || 0) > 0;
    if (drawingsExist) {
      setCropWarning(true);
      return;
    }
    setIsCropping(true);
  };
  const handleCropDone = () => {
    try {
      if (completedCrop) {
        // Create a temporary Image object to get the natural dimensions
        const imageElement = imgRef.current;

        const croppedDataUrl = getCroppedImage(imageElement, completedCrop);

        handleImageUploadToCanvas(croppedDataUrl);
        originalImageSrcRef.current = croppedDataUrl;
        // Now, you can use `croppedDataUrl` to do whatever you need,
        // without involving any canvas logic.

        // Close the cropper UI
        setIsCropping(false);
        setImageSrc(croppedDataUrl);
        if (!imageEchoGuardRef.current) syncImageSrc(croppedDataUrl);
        setCrop({
          unit: "%", // or "px"
          x: 10,
          y: 10,
          width: 50, // 50% of the image width
          height: 50,
        });
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleImageUpload(file);
  };

  const handleMeasurementAdd = (measurement) => {
    setMeasurements((prev) => {
      const i = prev.findIndex((m) => m.id === measurement.id);
      let next;

      if (i === -1) {
        // new measurement
        next = [...prev, measurement];
      } else {
        // update existing
        next = prev.slice();
        next[i] = { ...prev[i], ...measurement };
      }

      // broadcast to Yjs for both add & update
      syncMeasurements(next);

      return next;
    });
  };

  // ✅ Drop-in replacement for your effect
  useEffect(() => {
    const api = measurementCanvasRef.current;
    if (!api) return;

    // 1) take the CHILD as source of truth (measurements + polygons)
    const existing = api.getMergedMeasurements?.() || [];

    // 2) non-destructive merge: upsert incoming items by id, keep everything else
    const byId = new Map(existing.map((it) => [it?.id, it]));
    for (const it of Array.isArray(measurements) ? measurements : []) {
      if (!it || !it.id) continue;
      const prev = byId.get(it.id) || {};
      byId.set(it.id, { ...prev, ...it }); // upsert (no deletions)
    }

    const merged = Array.from(byId.values());

    // 3) load back into child (child will split into measurements & polygons)
    api.loadFromMergedMeasurements?.(merged);
  }, [measurements]);

  const handleZoomChange = (newZoom) => {
    setZoom(newZoom);
  };

  const handleImageTransform = async (toolId) => {
    if (!(toolId in ROTATION_BY_TOOL)) return; // non-rotation tools

    if (!imageSrc) {
      alert("No image loaded.");
      return;
    }
    const delta = ROTATION_BY_TOOL[toolId]; // e.g., 90 or -90
    const current = Number(cumulativeAngleRef.current || 0);
    const next = normalizeAngle(current + delta); // 0→90→180→270→0…

    // OPTIONAL: block if drawings exist
    const drawingsExist = (measurements?.length || 0) > 0;

    if (drawingsExist) {
      pendingAngleRef.current = next;
      setRotationWarning(true);
      return;
      // clear overlays if you want:
    } else {
      onTransFormHandel(ROTATION_BY_TOOL[toolId]);
    }
  };

  const handleToolClick = (delta) => {
    if (!imageSrc) {
      alert("No image loaded.");
      return;
    }

    const current = Number(cumulativeAngleRef.current || 0);
    const next = normalizeAngle(current + Number(delta)); // 0→90→180→270→0…

    const drawingsExist = (measurements?.length || 0) > 0;
    if (drawingsExist) {
      pendingAngleRef.current = next;
      setRotationWarning(true);
      return;
    }

    onTransFormHandel(next); // onTransFormHandel expects ABSOLUTE angle
  };

  const onTransFormHandel = async (absoluteAngle) => {
    setRotationWarning(false);
    const target = normalizeAngle(absoluteAngle ?? wrapperRotation ?? 0);

    // set ABSOLUTE angle; always rotate FROM original
    cumulativeAngleRef.current = target;
    const rotatedDataUrl =
      target === 0
        ? originalImageSrcRef.current
        : await rotateDataURL(originalImageSrcRef.current, target);

    handleImageUploadToCanvas(rotatedDataUrl);
    setImageSrc(rotatedDataUrl);
    setWrapperRotation(target);

    handleReset();

    pendingAngleRef.current = null;
    // ⬇️ NEW: broadcast absolute angle (no echo; de-duped)
    broadcastRotationIfNeeded(target);
  };

  const handleAngleInputChange = (val) => {
    const next = normalizeAngle(val ?? 0);
    setWrapperRotation(next); // keep UI in sync

    if (!imageSrc) return;

    const drawingsExist = (measurements?.length || 0) > 0;
    if (drawingsExist) {
      pendingAngleRef.current = next; // stash for modal
      setRotationWarning(true);
      return;
    }
    // debounce the actual rotate call
    if (rotateDebounceRef.current) clearTimeout(rotateDebounceRef.current);
    rotateDebounceRef.current = setTimeout(() => {
      onTransFormHandel(next); // rotate after delay
    }, ROTATE_INPUT_DELAY);
  };

  useEffect(
    () => () => {
      if (rotateDebounceRef.current) clearTimeout(rotateDebounceRef.current);
    },
    []
  );

  const handleAngleReset = () => {
    // cancel any scheduled rotation from the input
    if (rotateDebounceRef.current) clearTimeout(rotateDebounceRef.current);

    if (!imageSrc) return;
    if (wrapperRotation === 0) return;

    const drawingsExist = (measurements?.length || 0) > 0;
    if (drawingsExist) {
      pendingAngleRef.current = 0; // modal will rotate to 0 on confirm
      setResetWarning(true);
      return;
    }

    onTransFormHandel(0); // rotate immediately to absolute 0°
  };

  // Called by Toolbar: onMoveImage("up" | "down" | "left" | "right")
  const handleMoveImage = (direction) => {
    const STEP = 40; // screen pixels per click (tweak as you like)

    switch (direction) {
      case "up":
        panViewportBy(0, -STEP); // move image up on screen
        break;
      case "down":
        panViewportBy(0, STEP); // move image down on screen
        break;
      case "left":
        panViewportBy(-STEP, 0); // move image left on screen
        break;
      case "right":
        panViewportBy(STEP, 0); // move image right on screen
        break;
      default:
        break;
    }
  };

  const handleHistoryChange = useCallback((canUndoState, canRedoState) => {
    setCanUndo(canUndoState);
    setCanRedo(canRedoState);
  }, []);

  const handleSaveMeasurement = (measurement) => {
    // setSavedMeasurements((prev) => [...prev, measurement]);
    setSavedMeasurements((prev) => {
      const exists = prev.some((m) => m.id === measurement.id);
      const next = exists
        ? prev.map((m) =>
          m.id === measurement.id ? { ...m, ...measurement } : m
        )
        : [...prev, measurement];
      syncSavedMeasurements(next); // ← broadcast to others
      return next;
    });
  };

  const handleDeleteSavedMeasurement = (id) => {
    // setSavedMeasurements((prev) => prev.filter((m) => m.id !== id));
    setSavedMeasurements((prev) => {
      const next = prev.filter((m) => m.id !== id);
      syncSavedMeasurements(next); // ← broadcast deletion
      return next;
    });

    setMeasurements((prev) => {
      const next = prev.filter((m) => m.id !== id);
      syncMeasurements(next); // ← add this
      return next;
    });
    measurementCanvasRef.current?.removeById?.(id);
  };

  const handleRemoveActiveMeasurement = (measurementId) => {
    // setMeasurements((prev) => prev.filter((m) => m.id !== measurementId));
    setMeasurements((prev) => {
      const next = prev.filter((m) => m.id !== measurementId);
      syncMeasurements(next); // ← add this
      return next;
    });
  };

  // --- Add Comment ---
  const handleAddComment = (comment) => {
    const newComment = {
      ...comment,
      id: Date.now().toString(),
      timestamp: new Date(),
      author: userdata?.name || "unknown",
    };
    setComments((prev) => {
      const updated = [...prev, newComment];
      addComment(newComment);
      return updated;
    });
  };

  // --- Edit Comment or Reply ---
  const handleEditComment = (id, text, isReply, parentId) => {
    setComments((prev) => {
      let updated;
      if (isReply && parentId) {
        updated = prev.map((c) =>
          c.id === parentId
            ? {
              ...c,
              replies: c.replies?.map((r) =>
                r.id === id ? { ...r, text } : r
              ),
            }
            : c
        );
        // granular remote sync for the reply only
        updateReplyRemote(parentId, id, { text });
        if (threadComment?.id === parentId) {
          setThreadComment((prevT) =>
            prevT
              ? {
                ...prevT,
                replies: prevT.replies?.map((r) =>
                  r.id === id ? { ...r, text } : r
                ),
              }
              : null
          );
        }
      } else {
        updated = prev.map((c) => (c.id === id ? { ...c, text } : c));
        // granular remote sync for the top-level comment
        const changed = updated.find((c) => c.id === id);
        if (changed) updateComment(changed);

        if (threadComment?.id === id) {
          setThreadComment((prevT) => (prevT ? { ...prevT, text } : null));
        }
      }

      return updated;
    });
  };

  // --- Delete Comment ---
  const handleDeleteComment = (id) => {
    setComments((prev) => {
      const updated = prev.filter((c) => c.id !== id);
      deleteCommentRemote(id); // granular remote delete
      return updated;
    });
    if (threadComment?.id === id) {
      setShowCommentThread(false);
      setThreadComment(null);
    }
  };

  // --- Resolve Comment ---
  const handleResolveComment = (id) => {
    setComments((prev) => {
      const updated = prev.map((c) =>
        c.id === id ? { ...c, isResolved: true } : c
      );
      // granular remote resolve (defaults to { isResolved: true })
      resolveCommentRemote(id);
      return updated;
    });
  };

  // --- Add Reply ---
  const handleAddReply = (parentId, text) => {
    const newReply = {
      id: Date.now().toString(),
      text,
      timestamp: new Date(),
      author: userdata?.name || "User",
    };

    setComments((prev) => {
      const updated = prev.map((c) =>
        c.id === parentId
          ? { ...c, replies: [...(c.replies || []), newReply] }
          : c
      );
      // granular remote add reply
      addReplyRemote(parentId, newReply);
      return updated;
    });

    if (threadComment?.id === parentId) {
      setThreadComment((prev) =>
        prev ? { ...prev, replies: [...(prev.replies || []), newReply] } : null
      );
    }
  };

  const handleToolChange = (tool) => {
    if (tool === "delete") {
      deleteSelected();
      setActiveTool("select");
    } else if (tool === "open") {
      handleOnOpenPolygon();
    } else {
      setActiveTool(tool);
      if (tool === "comment") {
        setShowCommentPanel(true);
      } else {
        setShowCommentPanel(false);
      }
    }
  };

  const handleToggleCommentPanel = () => {
    setShowCommentPanel(!showCommentPanel);
  };

  // Utility: convert a canvas-space point to page-space using the live canvas rect
  function canvasToPage(pos) {
    const rect =
      measurementCanvasRef.current?.getCanvasClientRect?.() ||
      document
        .querySelector("#canvas-container canvas")
        ?.getBoundingClientRect();

    if (!rect || !pos) return { x: 0, y: 0 };
    const sx = rect.width / (canvasSize?.width || 1);
    const sy = rect.height / (canvasSize?.height || 1);
    return { x: rect.left + pos.x * sx, y: rect.top + pos.y * sy };
  }

  // Utility: clamp any page-space point inside #zoom-root
  function clampToZoomRoot(pos, overlayW, overlayH, pad = 8) {
    const zr = document.getElementById("zoom-root")?.getBoundingClientRect();
    if (!zr || !pos) return pos || { x: 0, y: 0 };

    const minL = zr.left + pad;
    const minT = zr.top + pad;
    const maxL = zr.right - pad - (overlayW || 0);
    const maxT = zr.bottom - pad - (overlayH || 0);

    return {
      x: Math.max(minL, Math.min(pos.x, maxL)),
      y: Math.max(minT, Math.min(pos.y, maxT)),
    };
  }

  // Detect if a point is already in page-space (roughly within/near the canvas)
  function isPagePoint(pos) {
    const rect =
      measurementCanvasRef.current?.getCanvasClientRect?.() ||
      document
        .querySelector("#canvas-container canvas")
        ?.getBoundingClientRect();
    if (!rect || !pos) return false;
    return (
      pos.x >= rect.left - 50 &&
      pos.x <= rect.right + 50 &&
      pos.y >= rect.top - 50 &&
      pos.y <= rect.bottom + 50
    );
  }

  const handleShowCommentInput = (show, position) => {
    setShowCommentInput(show);
    if (!position) return;

    const pagePos = isPagePoint(position) ? position : canvasToPage(position);
    // small offset near the pin
    const raw = { x: pagePos.x + 12, y: pagePos.y - 12 };
    const clamped = clampToZoomRoot(raw, 300, 64);
    setCommentInputPosition(clamped);
  };

  const handleShowCommentTooltip = (show, comment, position) => {
    setShowCommentTooltip(show);
    if (comment) setTooltipComment(comment);
    if (!position) return;

    const pagePos = isPagePoint(position) ? position : canvasToPage(position);
    const raw = { x: pagePos.x + 12, y: pagePos.y - 12 };
    const clamped = clampToZoomRoot(raw, 260, 110);
    setTooltipPosition(clamped);
  };

  const handleShowCommentThread = (show, comment, position) => {
    setShowCommentThread(show);
    if (comment) setThreadComment(comment);
    if (!position) return;

    const pagePos = isPagePoint(position) ? position : canvasToPage(position);
    const raw = { x: pagePos.x + 16, y: pagePos.y - 16 };
    const clamped = clampToZoomRoot(raw, 380, 420);
    setThreadPosition(clamped);
  };

  function pageToCanvas(pos) {
    if (!pos) return { x: 0, y: 0 };

    // get the live canvas client rect (upper or lower fabric canvas)
    const el =
      document.querySelector("#canvas-container canvas") ||
      document.querySelector("canvas");
    const rect = el?.getBoundingClientRect?.();
    if (!rect) return { x: pos.x, y: pos.y };

    const sx = (window.__canvasLogicalW || canvasSize?.width || 1) / rect.width;
    const sy =
      (window.__canvasLogicalH || canvasSize?.height || 1) / rect.height;

    return {
      x: (pos.x - rect.left) * sx,
      y: (pos.y - rect.top) * sy,
    };
  }

  const updateMeasurementRunTime = (updated) => {
    const updates = Array.isArray(updated) ? updated : [updated];

    // Find correct updated object based on selected ID
    const u = updates.find((item) => item.id === selectedObjectId);
    if (!u) return;

    setSavedMeasurements((prev) => {
      const next = prev.map((s) => {
        if (s.id !== u.id) return s;

        let type = s.type;
        let newValue = s.value;

        // --- Detect type using object keys ---
        if ("start" in u && "end" in u) {
          type = "linear";
          newValue = `${u.distance?.toFixed(2)} ${u.unit}`;
        } else if ("center" in u && "point1" in u && "point2" in u) {
          console.log("u>>>>>>>", u);
          type = "angle";
          newValue = `${u.angle?.toFixed(1)}°`;
          // 🧭 Also update arm distances
          s.arm1Distance = u.arm1Distance;
          s.arm2Distance = u.arm2Distance;
        } else if ("points" in u && "area" in u) {
          type = "area";
          newValue = `A: ${u.area?.toFixed(2)} ${u.unit
            }  (P: ${u.perimeter?.toFixed(2)} ${u.unit})`;
        }

        return { ...s, ...u, type, value: newValue };
      });
      // 🔄 broadcast saved history runtime change
      syncSavedMeasurements(next);

      return next;
    });

    setMeasurements((prev) => {
      const updates = Array.isArray(updated) ? updated : [updated];

      const updatedList = prev.map((m) => {
        const found = updates.find((u) => u.id === m.id);
        return found ? { ...m, ...found } : m;
      });

      // add any new ones not in prev
      const newOnes = updates.filter((u) => !prev.some((m) => m.id === u.id));
      const next = [...updatedList, ...newOnes];

      syncMeasurements(next); // ← add this (simple version)
      return next;
      // return [...updatedList, ...newOnes];
    });
  };

  // callback to get canvas ref from the child
  const saveCanvasReferenceForOtherComponents = useCallback(
    (incomingCanvas) => {
      if (!incomingCanvas) return;
      canvasRef.current = incomingCanvas; // no optional chaining on the LHS
    },
    []
  );

  // ---- APPLY changes live as slider moves
  const redrawBg = (canvas, bg) => {
    if (!canvas || !bg) return;

    // Fabric v5 style
    if (typeof canvas.setBackgroundImage === "function") {
      canvas.setBackgroundImage(bg, () => canvas.requestRenderAll());
      return;
    }

    // Fabric v6 style: assign + mark dirty + render
    canvas.backgroundImage = bg;
    bg.dirty = true;
    canvas.requestRenderAll();
  };

  const toggleInvert = () => {
    const canvas = canvasRef.current;
    const bg = canvas?.backgroundImage;
    if (!canvas || !bg) return;

    const Invert = fabric.filters?.Invert || fabric.Image?.filters?.Invert;
    if (!Invert) {
      console.warn("Invert filter not available in this Fabric build.");
      return;
    }

    bg.filters = bg.filters || [];
    const SLOT = 3; // reserve slot 3 for invert
    const isOn = !!bg.filters[SLOT];

    bg.filters[SLOT] = isOn ? null : new Invert();
    bg.applyFilters();
    redrawBg(canvas, bg);
  };

  // Toggle Sharpen (no slider). Works on Fabric v5/v6.
  const toggleSharpen = () => {
    const canvas = canvasRef.current;
    const bg = canvas?.backgroundImage;
    if (!canvas || !bg) return;

    const Convolute =
      fabric.filters?.Convolute || fabric.Image?.filters?.Convolute;
    const Sharpen = fabric.filters?.Sharpen || fabric.Image?.filters?.Sharpen;

    bg.filters = Array.isArray(bg.filters) ? bg.filters : [];
    const SLOT = 2; // keep your sharpen slot
    const isOn = !!bg.filters[SLOT];

    if (isOn) {
      // OFF
      bg.filters[SLOT] = null;
    } else {
      // ON — prefer Convolute with your previously-working kernel
      if (Convolute) {
        const KERNEL = [1, 1, 1, 1, 0.7, -1, -1, -1, -1];
        bg.filters[SLOT] = new Convolute({ matrix: KERNEL }); // no opaque/normalize
      } else if (Sharpen) {
        bg.filters[SLOT] = new Sharpen(); // fallback if Convolute not bundled
      } else {
        console.warn(
          "Sharpen/Convolute filter not available in this Fabric build."
        );
        return;
      }
    }

    // apply + redraw (v5/v6 safe)
    bg.applyFilters?.();
    if (typeof canvas.setBackgroundImage === "function") {
      canvas.setBackgroundImage(bg, () => canvas.requestRenderAll());
    } else {
      canvas.backgroundImage = bg;
      bg.dirty = true;
      canvas.requestRenderAll();
    }
  };

  // Move the viewport by screen pixels (auto-scales by current zoom)
  const panViewportBy = (dxScreen, dyScreen) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const t = canvas.viewportTransform ?? [1, 0, 0, 1, 0, 0];
    const zoom = t[0] || 1;

    // convert screen px -> canvas-space px
    t[4] += dxScreen / zoom;
    t[5] += dyScreen / zoom;

    canvas.setViewportTransform(t);
    canvas.requestRenderAll();
  };
  const handleReset = () => {
    // 1) ask the child to clear Fabric overlays + internal state + history
    measurementCanvasRef.current?.resetScene?.({
      resetBg: false, // keep bg pose/angle/scale
      clearBgFilters: true, // strip bg filters
    });

    // 2) clear parent-side state that drives panels/history
    // setMeasurements([]); // active list in right panel
    setMeasurements(() => {
      const next = [];
      syncMeasurements(next); // ← add this
      return next;
    });

    setSavedMeasurements(() => {
      const next = [];
      syncSavedMeasurements(next); // keep peers in sync
      return next;
    });
    setComments([]); // comment panel/thread
    setZoom(1); // UI zoom readout
    setActiveTool("select"); // put tools in a neutral state
    resetImageProps();
    resetCurves();
    resetSmartSharpen();
    onClearCamparedResult();

    // optional: if you track transforms/rotation at parent UI level
    setImageTransforms((prev) => ({ ...prev /* keep as-is */ }));
  };
  const deleteSelected = () => {
    // setMeasurements((prev) => prev.filter((el) => el.id !== selectedObjectId));
    setMeasurements((prev) => {
      const next = prev.filter((el) => el.id !== selectedObjectId);
      syncMeasurements(next); // ← add this
      return next;
    });
    setSavedMeasurements((prev) => {
      const next = prev.filter((el) => el.id !== selectedObjectId);
      syncSavedMeasurements(next); // ← add this
      return next;
    });
    measurementCanvasRef.current?.deleteSelection?.();
  };

  /** Contrast: slot 0, -1..1 (neutral 0) */
  const setCanvasContrast = (v) => {
    setImageControls((s) => ({
      ...s,
      contrast: {
        ...s.contrast,
        value: clamp(v, s.contrast.min, s.contrast.max),
      },
    }));
    const { canvas, bg } = getBG();
    if (!canvas || !bg) return;

    const Contrast =
      fabric.filters?.Contrast || fabric.Image?.filters?.Contrast;
    if (!Contrast) return;

    bg.filters = bg.filters || [];
    const val = clamp(v, -1, 1);
    bg.filters[0] = val !== 0 ? new Contrast({ contrast: val }) : null;
    bg.applyFilters?.();
    canvas.requestRenderAll?.();
  };

  /** Brightness: slot 1, -1..1 (neutral 0) */
  const setCanvasBrightness = (v) => {
    setImageControls((s) => ({
      ...s,
      brightness: {
        ...s.brightness,
        value: clamp(v, s.brightness.min, s.brightness.max),
      },
    }));
    const { canvas, bg } = getBG();
    if (!canvas || !bg) return;

    const Brightness =
      fabric.filters?.Brightness || fabric.Image?.filters?.Brightness;
    if (!Brightness) return;

    bg.filters = bg.filters || [];
    const val = clamp(v, -1, 1);
    bg.filters[1] = val !== 0 ? new Brightness({ brightness: val }) : null;
    bg.applyFilters?.();
    canvas.requestRenderAll?.();
  };

  /** Zoom: viewport scale (neutral 1, 0.25..4) */
  const setCanvasZoom = (v) => {
    setImageControls((s) => ({
      ...s,
      zoom: { ...s.zoom, value: clamp(v, s.zoom.min, s.zoom.max) },
    }));
    const canvas = canvasRef.current;
    if (!canvas) return;
    const z = clamp(v, 0.25, 4);
    const cx = canvas.getWidth() / 2;
    const cy = canvas.getHeight() / 2;

    if (typeof canvas.zoomToPoint === "function" && fabric?.Point) {
      canvas.zoomToPoint(new fabric.Point(cx, cy), z);
    } else {
      const t = canvas.viewportTransform ?? [1, 0, 0, 1, 0, 0];
      const tx = t[4],
        ty = t[5];
      canvas.setViewportTransform([z, 0, 0, z, tx, ty]);
    }
    canvas.requestRenderAll?.();
  };

  const resetImageProps = () => {
    setImageControls({
      contrast: { min: -1, max: 1, step: 0.05, neutral: 0, value: 0 },
      brightness: { min: -1, max: 1, step: 0.05, neutral: 0, value: 0 },
      invert: { min: 0, max: 1, step: 0.01, neutral: 0, value: 0 },
      sharpness: { min: 0, max: 3, step: 0.05, neutral: 0, value: 0 },
      zoom: { min: 0.25, max: 4, step: 0.05, neutral: 1, value: 1 },
    });

    const canvas = canvasRef.current;
    if (!canvas) return;
    const bg = canvas.backgroundImage;

    if (bg) {
      // slots: 0 contrast, 1 brightness, 2 sharpness, 3 invert
      bg.filters = [null, null, null, null];
      bg.applyFilters?.();
    }

    const t = canvas.viewportTransform ?? [1, 0, 0, 1, 0, 0];
    canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    canvas.requestRenderAll?.();
  };

  // Recompute saved measurements when unit/scale changes

  useEffect(() => {
    const unitOn = Number(scale) > 0 && Number(scaleLength) > 0;

    setMeasurements((prev) => {
      const list = Array.isArray(prev) ? prev : [];

      const next = list.map((m) => {
        if (m.start && m.end) {
          const distance = distUnits(m.start, m.end, { scale, scaleLength });
          return { ...m, distance, unit: unitOn ? "mm" : "px" };
        }
        if (Array.isArray(m.points) && m.points.length >= 2) {
          const perimeter = calculatePolygonPerimeter(m.points, (a, b) =>
            distUnits(a, b, { scale, scaleLength })
          );
          const canArea = m.isClosed !== false && m.points.length >= 3;
          const area = canArea
            ? calculatePolygonArea(m.points, scale, scaleLength, canvasRef)
            : 0;
          return {
            ...m,
            perimeter,
            perimeterUnit: unitOn ? "mm" : "px",
            area,
            ...(canArea
              ? { areaUnit: unitOn ? "mm²" : "px²" }
              : { areaUnit: undefined }),
          };
        }
        if (m.center && m.point1 && m.point2) {
          const arm1Distance = distUnits(m.center, m.point1, {
            scale,
            scaleLength,
          });
          const arm2Distance = distUnits(m.center, m.point2, {
            scale,
            scaleLength,
          });
          const chordDistance =
            m.chordDistance !== undefined
              ? distUnits(m.point1, m.point2, { scale, scaleLength })
              : undefined;
          return {
            ...m,
            arm1Distance,
            arm2Distance,
            ...(chordDistance !== undefined ? { chordDistance } : {}),
            unit: unitOn ? "mm" : "px",
          };
        }
        return m;
      });

      return next;
    });

    // ⛔️ DO NOT call syncMeasurements(next) here
    // If you want collaborators to share scale/unit, broadcast via syncScale({ unit, scale, scaleLength }) instead.
  }, [scale, scaleLength]);

  // UI state (unchanged)

  const applyCurvesFilters = () => {
    const { canvas, bg } = getBG(); // your helper
    if (!canvas || !bg) return;

    bg.filters = bg.filters || [];

    const ColorMatrix =
      (fabric.filters && fabric.filters.ColorMatrix) ||
      (fabric.Image &&
        fabric.Image.filters &&
        fabric.Image.filters.ColorMatrix);

    const Gamma =
      (fabric.filters && fabric.filters.Gamma) ||
      (fabric.Image && fabric.Image.filters && fabric.Image.filters.Gamma);

    // sliders (normalized)
    const s = clamp(curvesRef.current.shadows, -1, 1); // shadows
    const m = clamp(curvesRef.current.midtones, -1, 1); // midtones
    const h = clamp(curvesRef.current.highlights, -1, 1); // highlights

    // --- DISTINCT MAPPINGS ---

    // 1) SHADOWS: lift/crush blacks, but compress contrast a bit so highlights are protected
    //    - Positive s: lift blacks (b>0) and reduce contrast (c<1)
    //    - Negative s: crush blacks (b<0) and boost contrast a touch (c>1)
    const b_shadows = s * 0.25; // brightness offset
    const c_shadows = 1 - Math.sign(s) * Math.abs(s) * 0.2; // 0.8..1.2

    // 2) HIGHLIGHTS: brighten/pull back brights, protect blacks
    //    - Positive h: raise contrast (c>1) but slightly lower global brightness (b<0)
    //    - Negative h: lower contrast (c<1) and slightly raise brightness (b>0)
    const c_highs = 1 + h * 0.5; // 0.5..1.5
    const b_highs = -Math.sign(h) * Math.abs(h) * 0.1;

    // Combine shadows/highlights into ONE ColorMatrix so they interact naturally.
    // We use average of the two contrasts and sum of offsets.
    const c_total = clamp((c_shadows + c_highs) / 2, 0.5, 1.5);
    const b_total = clamp(b_shadows + b_highs, -0.3, 0.3);

    // 3) MIDTONES: gamma is perfect for “middle gray”
    //    m>0 → g<1 (brighten mids), m<0 → g>1 (darken mids)
    const g_mid = clamp(1 - m * 0.6, 0.2, 3);

    // slot 4: brightness/contrast via ColorMatrix (affects blacks/whites bias)
    if (ColorMatrix) {
      const mat = [
        c_total,
        0,
        0,
        0,
        b_total,
        0,
        c_total,
        0,
        0,
        b_total,
        0,
        0,
        c_total,
        0,
        b_total,
        0,
        0,
        0,
        1,
        0,
      ];
      bg.filters[4] = new ColorMatrix({ matrix: mat });
    }

    // slot 5: gamma for midtones
    if (Gamma) {
      bg.filters[5] = new Gamma({ gamma: [g_mid, g_mid, g_mid] });
    }

    bg.applyFilters?.();
    canvas.requestRenderAll?.();
  };

  // Handlers (unchanged)
  const handleCurvesShadows = (v) => {
    const val = clamp(Number(v) || 0, -1, 1);
    setCurves((p) => ({ ...p, shadows: val }));
    curvesRef.current.shadows = val;
    applyCurvesFilters();
  };
  const handleCurvesMidtones = (v) => {
    const val = clamp(Number(v) || 0, -1, 1);
    setCurves((p) => ({ ...p, midtones: val }));
    curvesRef.current.midtones = val;
    applyCurvesFilters();
  };
  const handleCurvesHighlights = (v) => {
    const val = clamp(Number(v) || 0, -1, 1);
    setCurves((p) => ({ ...p, highlights: val }));
    curvesRef.current.highlights = val;
    applyCurvesFilters();
  };
  const resetCurves = () => {
    setCurves({ shadows: 0, midtones: 0, highlights: 0 });
    curvesRef.current = { shadows: 0, midtones: 0, highlights: 0 };
    applyCurvesFilters();
  };

  const onSharpenAmountChange = (v) => {
    const { canvas, bg } = getBG();
    if (!canvas || !bg) return;

    setSharpenAmount(v);

    const Convolute =
      fabric.filters?.Convolute || fabric.Image?.filters?.Convolute;
    if (!Convolute) return;

    const k = clamp(v, -1, 1); // -1 softens, +1 sharpens
    const center = 1 + 4 * k;
    const side = -k;

    bg.filters[4] =
      k !== 0
        ? new Convolute({
          matrix: [0, side, 0, side, center, side, 0, side, 0],
        })
        : null;

    bg.applyFilters();
    canvas.requestRenderAll();
  };

  const onSharpenRadiusChange = (v) => {
    const { canvas, bg } = getBG();
    if (!canvas || !bg) return;

    setSharpenRadius(v);

    const Convolute =
      fabric.filters?.Convolute || fabric.Image?.filters?.Convolute;
    if (!Convolute) return;

    const r = clamp(v, -1, 1);
    const center = 1 + 8 * r;
    const side = -r;

    bg.filters[5] =
      r !== 0
        ? new Convolute({
          matrix: [side, side, side, side, center, side, side, side, side],
        })
        : null;

    bg.applyFilters();
    canvas.requestRenderAll();
  };

  const onSharpenNoiseChange = (v) => {
    const { canvas, bg } = getBG();
    if (!canvas || !bg) return;

    setSharpenNoise(v);

    const Blur = fabric.filters?.Blur || fabric.Image?.filters?.Blur;
    if (!Blur) return;

    bg.filters[6] =
      v !== 0 ? new Blur({ blur: Math.abs(clamp(v, -1, 1)) * 0.5 }) : null;

    bg.applyFilters();
    canvas.requestRenderAll();
  };

  // Optional “Reset” for the whole block
  const resetSmartSharpen = () => {
    setSharpenAmount(0);
    setSharpenRadius(0);
    setSharpenNoise(0);
  };

  // Levels (black, mid, white in 0..100)
  const midToGamma = (m) => {
    const x = Math.min(99, Math.max(1, Number(m) || 50));
    return Math.pow(2, (50 - x) / 25);
  };

  const applyLevelsAdjustments = (lvls) => {
    const { canvas, bg } = getBG();
    if (!canvas || !bg) return;

    const ColorMatrix =
      (fabric.filters && fabric.filters.ColorMatrix) ||
      (fabric.Image &&
        fabric.Image.filters &&
        fabric.Image.filters.ColorMatrix);
    const Gamma =
      (fabric.filters && fabric.filters.Gamma) ||
      (fabric.Image && fabric.Image.filters && fabric.Image.filters.Gamma);

    bg.filters = Array.isArray(bg.filters) ? bg.filters : [];

    const inBlack = Math.min(0.99, Math.max(0, lvls.black / 100));
    const inWhite = Math.min(1, Math.max(inBlack + 0.01, lvls.white / 100));
    const s = 1 / (inWhite - inBlack);
    const b = -inBlack * s;

    const SLOT_MATRIX = 7;
    const SLOT_GAMMA = 8;

    if (ColorMatrix) {
      bg.filters[SLOT_MATRIX] = new ColorMatrix({
        matrix: [s, 0, 0, 0, b, 0, s, 0, 0, b, 0, 0, s, 0, b, 0, 0, 0, 1, 0],
      });
    }
    if (Gamma) {
      const g = midToGamma(lvls.mid);
      bg.filters[SLOT_GAMMA] = new Gamma({ gamma: [g, g, g] });
    }

    bg.applyFilters?.();
    canvas.requestRenderAll?.();
  };

  const computeLevelsHistogram = (lvls) => {
    const { bg } = getBG();
    if (!bg) return;

    const el = bg.getElement?.() || bg._element;
    if (!el) return;

    const w = el.naturalWidth || el.width;
    const h = el.naturalHeight || el.height;
    if (!w || !h) return;

    const off = document.createElement("canvas");
    const ctx = off.getContext("2d", { willReadFrequently: true });
    off.width = w;
    off.height = h;
    ctx.drawImage(el, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);

    const inBlack = Math.min(0.99, Math.max(0, lvls.black / 100));
    const inWhite = Math.min(1, Math.max(inBlack + 0.01, lvls.white / 100));
    const scale = 1 / (inWhite - inBlack);
    const offset = -inBlack * scale;
    const gamma = midToGamma(lvls.mid);

    const bins = new Array(256).fill(0);
    const stride = Math.max(1, Math.floor((w * h) / 300000));

    for (let i = 0; i < data.length; i += 4 * stride) {
      const r = data[i] / 255;
      const g = data[i + 1] / 255;
      const b = data[i + 2] / 255;
      let y = 0.2126 * r + 0.7152 * g + 0.0722 * b; // luminance
      y = Math.max(0, Math.min(1, y * scale + offset));
      y = Math.pow(y, 1 / gamma);
      const bin = Math.min(255, Math.max(0, Math.round(y * 255)));
      bins[bin]++;
    }
    setLevelsHistogram(bins);
  };
  const scheduleApplyLevels = (lvls) => {
    levelsPending.current = lvls;
    if (levelsApplyRAF.current) return; // already scheduled
    levelsApplyRAF.current = requestAnimationFrame(() => {
      levelsApplyRAF.current = 0;
      const next = levelsPending.current || levelsAdjustments;
      applyLevelsAdjustments(next);
      computeLevelsHistogram(next);
    });
  };

  const handleLevelAdjustment = (values) => {
    const [blk, mid, wht] = values;
    const b1 = Math.max(0, Math.min(98, Number(blk)));
    const w1 = Math.max(b1 + 2, Math.min(100, Number(wht)));
    const m1 = Math.max(b1 + 1, Math.min(w1 - 1, Number(mid)));
    const next = { black: b1, mid: m1, white: w1 };
    setLevelsAdjustments(next); // state for UI
    scheduleApplyLevels(next); // render + histogram using CURRENT values
  };

  const resetLevelsAdjustments = () => {
    const next = { black: 0, mid: 50, white: 100 };
    setLevelsAdjustments(next);
    scheduleApplyLevels(next);
  };

  // initial histogram once image is present
  useEffect(() => {
    computeLevelsHistogram(levelsAdjustments);
  }, [canvasRef]); // or whatever you use for the loaded background

  const [saveProjectLoading, setSaveProjectLoading] = useState(false);
  // call this on your "Save Project" button
  const handleSaveProjectDataApi = async () => {
    if (!projectId) return;

    const mergedMeasurements =
      measurementCanvasRef.current?.getMergedMeasurements?.();

    // 🚫 exclude imported shapes from being saved
    const measurementsToSave = mergedMeasurements.filter(
      (m) => m?.isImport !== true
    );

    const textAnnotations =
      measurementCanvasRef.current?.getTextAnnotations?.() || [];

    if (!mergedMeasurements || !Array.isArray(mergedMeasurements)) return;

    setSaveProjectLoading(true);

    const dataUrl = originalImageSrcRef.current || null;

    const guidelines = measurementCanvasRef.current?.getGuidelines?.() || [];
    const dots = measurementCanvasRef.current?.getDots?.() || [];
    const arrows = measurementCanvasRef.current?.getArrows?.() || [];

    // store shapes only, in the same nesting as your example
    const snapshot = {
      version: 1,
      background: {
        imageSrc: dataUrl, // null if the current image isn't base64,
        baseImageData: imageBase64StringObj,
      },
      overlays: {
        measurements: measurementsToSave,
        savedMeasurements: savedMeasurements,
        rotationAngle: cumulativeAngleRef?.current,
        guidelines,
        textAnnotations,
        comments,
        dots,
        arrows,
        scale,
        unit,
        scaleLength,
      },
    };
    const response = await addJsonToAnalysisProject({
      id: projectId,
      json: snapshot,
    });
    if (response.status === 200) {
      if (selectedTab !== 2) {
        takeThumbnailScreenshot();
        await uploadThumbNailScreenshot(projectId);
      }

      // dispatch(_onSaveProjectSnapshot({ projectId, snapshot }));
      // message.success("Project has been saved");
    }

    setSaveProjectLoading(false);
  };

  // inside your component
  const saveRef = useRef(handleSaveProjectDataApi);

  // keep the latest function in a ref so the interval always calls fresh logic
  useEffect(() => {
    saveRef.current = handleSaveProjectDataApi;
  }, [handleSaveProjectDataApi]);

  // start the interval once, clean it up on unmount
  useEffect(() => {
    const id = setInterval(() => {
      saveRef.current?.(); // runs every 20,000 ms
    }, 20000);

    return () => clearInterval(id);
  }, []);

  // load an <img> from a base64 dataURL
  const loadImgFromDataURL = async (dataUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = dataUrl; // base64
    });
  };

  // rotate a base64 dataURL by deltaDeg and return a new base64 (same MIME)
  const rotateDataURL = async (dataUrl, deltaDeg) => {
    const img = await loadImgFromDataURL(dataUrl);
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;

    const rad = ((deltaDeg % 360) * Math.PI) / 180;
    const a = Math.abs(rad);
    const cos = Math.cos(a);
    const sin = Math.sin(a);

    // size of rotated bounding box
    const bw = Math.abs(iw * cos) + Math.abs(ih * sin);
    const bh = Math.abs(iw * sin) + Math.abs(ih * cos);

    // offscreen canvas used only for pixel processing
    const c = document.createElement("canvas");
    c.width = Math.round(bw);
    c.height = Math.round(bh);

    const g = c.getContext("2d", { alpha: true });
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = "high";

    g.translate(c.width / 2, c.height / 2);
    g.rotate(rad);
    g.drawImage(img, -iw / 2, -ih / 2);

    // const mimeMatch = /^data:([^;]+);base64,/.exec(dataUrl);
    // const mime = mimeMatch ? mimeMatch[1] : "image/png";

    // return c.toDataURL(mime, 0.92);
    return c.toDataURL("image/png", 0.92);
  };

  const centerBgForPreview = () => {
    const c = canvasRef?.current;
    const bg = c?.backgroundImage;
    if (!c || !bg) return;

    bg.set({ originX: "center", originY: "center" });
    const { left, top } = c.getCenter();
    bg.set({ left, top });
    bg.set({ objectCaching: false });
    c.requestRenderAll();
  };

  function previewRotateToAbsolute(absAngle) {
    const a = normalizeAngle(absAngle);
    const baked = Number(previewBakedRef.current || 0);

    // rotate visually only by the delta from baked
    const visual = normalizeAngle(a - baked);

    setWrapperRotation(a); // keep the UI readout in absolute degrees

    const bg = canvasRef?.current?.backgroundImage;
    if (bg) {
      bg.set({ angle: visual }); // no double-rotation
      canvasRef.current.requestRenderAll();
    }
  }

  function clearPreviewRotation() {
    const c = canvasRef?.current;
    const bg = c?.backgroundImage;
    if (!bg) return;
    bg.set({ angle: 0 }); // remove visual delta
    c.requestRenderAll();
  }

  const commitDragRotation = async (absAngle) => {
    const target = normalizeAngle(absAngle ?? 0);

    // encode FROM the original at absolute target
    cumulativeAngleRef.current = target;

    const src = originalImageSrcRef.current;
    const rotatedDataUrl =
      target === 0 ? src : await rotateDataURL(src, target);

    handleImageUploadToCanvas(rotatedDataUrl);
    setImageSrc(rotatedDataUrl);
    setWrapperRotation(target);

    handleReset(); // your existing reset
    pendingAngleRef.current = null;
    // ⬇️ NEW: broadcast absolute angle (no echo; de-duped)
    broadcastRotationIfNeeded(target);
  };

  const startScrub = (e) => {
    centerBgForPreview();

    // If drawings exist, keep your warning behavior
    const drawingsExist = (measurements?.length || 0) > 0;
    if (drawingsExist) {
      pendingAngleRef.current = Number(wrapperRotation || 0);
      setRotationWarning(true);
      return;
    }

    isScrubbingRef.current = true;
    startXRef.current = e.clientX;

    // store the baked angle present in pixels at the start
    previewBakedRef.current = Number(wrapperRotation || 0);
    // baseAngle is where the absolute angle should start from
    baseAngleRef.current = Number(wrapperRotation || 0);

    document.body.style.cursor = "ew-resize";
    window.addEventListener("mousemove", onScrubMove);
    window.addEventListener("mouseup", endScrub);
  };

  const onScrubMove = (e) => {
    if (!isScrubbingRef.current) return;

    const dx = e.clientX - startXRef.current;

    // compute the next ABSOLUTE angle from where we started
    const rawAbs = baseAngleRef.current + dx / PX_PER_DEG;
    const nextAbs = normalizeAngle(Math.round(rawAbs / STEP_DEG) * STEP_DEG);

    previewAngleRef.current = nextAbs;

    // preview ONLY the delta relative to baked, so no initial jump
    previewRotateToAbsolute(nextAbs);
  };

  const endScrub = async () => {
    if (!isScrubbingRef.current) return;

    isScrubbingRef.current = false;
    document.body.style.cursor = "";

    const finalAbs = Number(previewAngleRef.current ?? wrapperRotation ?? 0);

    // remove the visual delta, keep pose steady, then encode absolute
    clearPreviewRotation();
    previewRotateToAbsolute(finalAbs); // keep the visual pose while we encode
    await commitDragRotation(finalAbs);

    window.removeEventListener("mousemove", onScrubMove);
    window.removeEventListener("mouseup", endScrub);
  };

  const zoomViaSlider = (rawNext) => {
    _zoomSliderPending = rawNext;
    if (_zoomSliderRaf) return;

    _zoomSliderRaf = requestAnimationFrame(() => {
      _zoomSliderRaf = 0;
      const scroller = zoomShellRef.current?.firstElementChild; // ZoomOnly root <div>
      if (!scroller) return;

      // ZoomOnly DOM: [ spacer , layer(abs) -> content(scaled) ]
      const layer = scroller.lastElementChild;
      const content = layer?.firstElementChild;
      if (!content) return;

      const prev = Number(zoom || 1);
      const next = clamp(Number(_zoomSliderPending || 1), 1, 2); // slider path capped at 200%
      _zoomSliderPending = null;
      if (Math.abs(prev - next) < 1e-4) return;

      // anchor at viewport center (screen)
      const mx = scroller.clientWidth / 2;
      const my = scroller.clientHeight / 2;

      // content coords BEFORE zoom
      const cx = (scroller.scrollLeft + mx) / prev;
      const cy = (scroller.scrollTop + my) / prev;

      // --- immediate visual feedback (optimistic) ---
      // apply transform directly so you see the zoom this frame,
      // ZoomOnly will also update on the next effect tick.
      content.style.transformOrigin = "0 0";
      content.style.transform = `scale(${next})`;
      content.style.willChange = "transform";

      // compute new scroll to keep same content point centered
      const baseW = content.offsetWidth || 1; // natural size (not affected by transform)
      const baseH = content.offsetHeight || 1;

      const targetLeft = cx * next - mx;
      const targetTop = cy * next - my;

      // bound using computed new extent (don't wait for spacer reflow)
      const maxLeft = Math.max(0, baseW * next - scroller.clientWidth);
      const maxTop = Math.max(0, baseH * next - scroller.clientHeight);

      scroller.scrollLeft = clamp(targetLeft, 0, maxLeft);
      scroller.scrollTop = clamp(targetTop, 0, maxTop);

      // keep controlled state in sync (lets ZoomOnly update spacer next frame)
      setZoom(next);
    });
  };

  const onAppendPolygon = (obj) => {
    const normalized = normalizePolygonMinimal(obj);
    if (!normalized) return;
    measurementCanvasRef.current?.appendPolygons?.(normalized);

    // mirror in parent state (without duplicating by id)
    setMeasurements((prev) => {
      const i = prev.findIndex((m) => m.id === normalized.id);
      if (i !== -1) {
        const next = prev.slice();
        next[i] = { ...prev[i], ...normalized };
        return next;
      }
      return [...prev, normalized];
    });
  };

  const onClearCamparedResult = () => {
    setSelectedObjectId(null);
    measurementCanvasRef?.current?.clearImportedPolygons();
    setMeasurements((prev) => prev.filter((m) => m?.isImport !== true));
  };

  const handleOnOpenPolygon = () => {
    setActiveTool("open-polygon");
    measurementCanvasRef?.current?.enableOpenPolygonMode();
  };

  const handlePolygonEditStart = (id) => {
    setMeasurements((prev) => prev.filter((m) => m.id !== id));
    setSavedMeasurements((prev) => prev.filter((m) => m.id !== id));
    setSelectedToolId("");
  };

  // short cuts for canvas
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      const isTyping =
        tag === "input" ||
        tag === "textarea" ||
        t?.isContentEditable ||
        // also ignore if inside a contenteditable ancestor (rich editors)
        (t &&
          typeof t.closest === "function" &&
          t.closest("[contenteditable='true']"));

      if (isTyping) return;
      if (!e.metaKey && !e.ctrlKey) {
        if (e.key === "c" || e.key === "C") {
          e.preventDefault();
          setActiveTool("comment");
          setShowCommentPanel?.(true);
        } else if (e.key === "h" || e.key === "H") {
          e.preventDefault();
          setActiveTool?.("pan");
        } else if (e.key === "l" || e.key === "L") {
          e.preventDefault();
          setActiveTool("linear");
        } else if (e.key === "a" || e.key === "A") {
          e.preventDefault();
          setActiveTool("angle");
        } else if (e.key === "p" || e.key === "P") {
          e.preventDefault();
          setActiveTool("polygon");
        } else if (e.key === "t" || e.key === "T") {
          e.preventDefault();
          setActiveTool("text");
        } else if (e.key === "z" || e.key === "Z") {
          e.preventDefault();
          setActiveTool("scale");
        } else if (e.key === "v" || e.key === "V") {
          e.preventDefault();
          setActiveTool("select");
        } else if (e.key === "Escape") {
          e.preventDefault();
          setActiveTool("select");
          setShowCommentPanel?.(false);
        }
      } else {
        if (e.key === "z" || e.key === "Z") {
          e.preventDefault();
          measurementCanvasRef.current?.undoLastPolygonPoint?.();
          return;
        }
      }
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const exportToJson = async () => {
    if (savedMeasurements.length === 0) {
      message.warning("You do not have any saved history");
      return;
    }

    const exportObject = {
      version: "4.5.12",
      flags: {},
      shapes: [],
      imagePath: imageBase64StringObj.fileName,
      imageData: originalImageSrcRef?.current,
    };

    savedMeasurements.forEach((item) => {
      let shapeObj = {
        label: item.label,
        toothNumber: item.title,
        points: item.points.map(p => [
          p.x * (parseFloat(imageBase64StringObj.width) / canvasSize.width),
          p.y * (parseFloat(imageBase64StringObj.height) / canvasSize.height),
        ]),
        group_id: null,
        shape_type: "polygon",
        flags: {},
      };
      exportObject.shapes.push(shapeObj);
    });
    processImageAnalysisForLabelMe(exportObject);
  };

  const processImageAnalysisForLabelMe = async (data) => {
    data.imageHeight = parseFloat(imageBase64StringObj?.height);
    data.imageWidth = parseFloat(imageBase64StringObj?.width);
    data.imageData = data.imageData.replace("data:image/jpeg;base64,", "");
    data.imageData = data.imageData.replace("data:image/png;base64,", "");
    data.imageData = data.imageData.replace("data:image/jpg;base64,", "");
    data.imageData = data.imageData.replace("data:image/bmp;base64,", "");
    const jsonData = JSON.stringify(data, null, 2);

    const blob = new Blob([jsonData], { type: "application/json" });
    const fileName = imageBase64StringObj?.fileName
      .split(".")
      .slice(0, -1)
      .join(".");

    saveAs(blob, fileName + ".json");
    await downloadScreenshot();
  };
  // inside DentalMeasurementTool.jsx
  const handleHighlightPolygon = (idOrNull) => {
    // idOrNull can be a string id or null/undefined to clear
    measurementCanvasRef.current?.highlightPolygon?.(idOrNull ?? null);
  };

  // 1) Turn a DOM node into a JPEG blob
  async function nodeToJpegBlob(
    node,
    { backgroundColor = "white", quality = 0.95 } = {}
  ) {
    const dataUrl = await htmlToImage.toJpeg(node, {
      backgroundColor,
      quality,
    });
    const res = await fetch(dataUrl);
    return await res.blob(); // image/jpeg
  }

  // 2) Build FormData and call your existing API helper
  async function uploadThumbNailScreenshot(projectId) {
    try {
      const node = thumbnailReff.current; // your thumbnail node
      const blob = await nodeToJpegBlob(node, {
        backgroundColor: "white",
        quality: 0.95,
      });

      const formData = new FormData();
      formData.append("id", String(projectId));
      // filename is optional; server just needs field name "thumbnail"
      formData.append("thumbnail", blob, "thumbnail.jpg");

      const response = await addThumbnailToProject({ formData });
      if (response.status === 200) {
        console.log("thumnbnail uploaded");
      }
    } catch (err) {
      console.error(err);
    }
  }
  // flip a base64 image horizontaly and return a new base64
  const flipImageDataURL = async (dataUrl) => {
    const img = await loadImgFromDataURL(dataUrl); // you already use this in rotate
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;

    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d");

    // // mirror on Y
    // ctx.translate(0, h);
    // ctx.scale(1, -1);
    // ctx.drawImage(img, 0, 0, w, h);

    // mirror on X (horizontal flip)
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(img, 0, 0, w, h);

    return c.toDataURL("image/png", 0.92);
  };

  const handleFlipImage = async () => {
    if (!imageSrc) return;

    // If drawings exist, keep your warning behavior
    const drawingsExist = (measurements?.length || 0) > 0;
    if (drawingsExist) {
      setFlipWarning(true);
      return;
    }

    const src = imageSrc || originalImageSrcRef.current;
    const flipped = await flipImageDataURL(src);

    // reuse your existing image loader
    handleImageUploadToCanvas(flipped); // shows flipped image on Fabric bg
    setImageSrc(flipped); // state
    originalImageSrcRef.current = flipped; // make it the new baseline

    // 🔑 We just baked whatever rotation was visible into pixels.
    // Reset rotation so it won't be applied again on save/reload.
    cumulativeAngleRef.current = 0;
    setWrapperRotation(0);
    // if you broadcast rotation to collaborators, also broadcast zero:
    try {
      broadcastRotationIfNeeded?.(0);
    } catch { }
    await handleSaveProjectDataApi(); // persist JSON + thumbnail
  };
  const handleFlipHorizontal = async () => {
    if (!imageSrc) return;

    const src = imageSrc || originalImageSrcRef.current;
    const flipped = await flipImageDataURL(src);

    // reuse your existing image loader
    handleImageUploadToCanvas(flipped); // shows flipped image on Fabric bg
    setImageSrc(flipped); // state
    originalImageSrcRef.current = flipped; // make it the new baseline
    // 🔑 Same reset here
    cumulativeAngleRef.current = 0;
    setWrapperRotation(0);
    try {
      broadcastRotationIfNeeded?.(0);
    } catch { }
    await handleSaveProjectDataApi(); // persist JSON + thumbnail
  };

  const handleUploadNewImage = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (file) {
        setWrapperRotation(0);
        cumulativeAngleRef.current = null;
        handleImageUpload(file);
      }
    };
    input.click();
  };

  // keep this as-is
  const handleSelectColorForMeasurementObject = (pickedHex) => {
    applyColorToSelected(pickedHex);
  };

  // one function: apply color to the currently selected object
  const applyColorToSelected = (hex) => {
    if (!hex || !selectedObjectId) return;

    setSelectedColor(hex);

    // --- 1) Update active measurements (your original flow) ---
    let nextMeasurementsSnapshot = null; // capture to build merged later
    setMeasurements((prev) => {
      const next = Array.isArray(prev)
        ? prev.map((m) =>
          m?.id === selectedObjectId ? { ...m, color: hex } : m
        )
        : prev;

      nextMeasurementsSnapshot = next;

      // immediately update the canvas (so user sees it without waiting)
      try {
        measurementCanvasRef.current?.setMeasurementsOnly?.(next);
      } catch { }

      // keep your existing broadcast
      try {
        syncMeasurements?.(next);
      } catch { }

      return next;
    });

    // --- 2) If the same id exists in savedMeasurements, update there too ---
    setSavedMeasurements((prev) => {
      if (!Array.isArray(prev)) return prev;

      let found = false;
      const next = prev.map((m) => {
        if (m?.id === selectedObjectId) {
          found = true;
          return { ...m, color: hex };
        }
        return m;
      });

      if (found) {
        // Build a SINGLE merged array (active wins on id conflicts)
        const byId = new Map();
        (next || []).forEach((it) => it?.id && byId.set(it.id, it)); // saved first
        ((nextMeasurementsSnapshot ?? measurements) || []).forEach(
          (it) => it?.id && byId.set(it.id, it) // then active overrides
        );
        const merged = Array.from(byId.values());

        // 🔁 Reload merged into the canvas so saved-only polygons repaint with new color
        try {
          measurementCanvasRef.current?.loadFromMergedMeasurements?.(merged);
        } catch { }

        // keep your existing saved sync
        try {
          syncSavedMeasurements?.(next);
        } catch { }
      }

      return found ? next : prev;
    });
    setSelectedObjectId(null);
  };

  // line style selection
  const handleSelectLineStyleForMeasurementObject = (lineStyle) => {
    applyLineStyleToSelected(lineStyle);
  };

  // one function: apply linestyle to the currently selected object
  const applyLineStyleToSelected = (lineStyle) => {
    if (!lineStyle || !selectedObjectId) return;

    setSelectedLineStyle(lineStyle);

    // --- 1) Update active measurements (your original flow) ---
    let nextMeasurementsSnapshot = null; // capture to build merged later
    setMeasurements((prev) => {
      const next = Array.isArray(prev)
        ? prev.map((m) =>
          m?.id === selectedObjectId ? { ...m, lineStyle: lineStyle } : m
        )
        : prev;

      nextMeasurementsSnapshot = next;

      // immediately update the canvas (so user sees it without waiting)
      try {
        measurementCanvasRef.current?.setMeasurementsOnly?.(next);
      } catch { }

      // keep your existing broadcast
      try {
        syncMeasurements?.(next);
      } catch { }

      return next;
    });

    // --- 2) If the same id exists in savedMeasurements, update there too ---
    setSavedMeasurements((prev) => {
      if (!Array.isArray(prev)) return prev;

      let found = false;
      const next = prev.map((m) => {
        if (m?.id === selectedObjectId) {
          found = true;
          return { ...m, lineStyle: lineStyle };
        }
        return m;
      });

      if (found) {
        // Build a SINGLE merged array (active wins on id conflicts)
        const byId = new Map();
        (next || []).forEach((it) => it?.id && byId.set(it.id, it)); // saved first
        ((nextMeasurementsSnapshot ?? measurements) || []).forEach(
          (it) => it?.id && byId.set(it.id, it) // then active overrides
        );
        const merged = Array.from(byId.values());

        // 🔁 Reload merged into the canvas so saved-only polygons repaint with new color
        try {
          measurementCanvasRef.current?.loadFromMergedMeasurements?.(merged);
        } catch { }

        // keep your existing saved sync
        try {
          syncSavedMeasurements?.(next);
        } catch { }
      }

      return found ? next : prev;
    });
    setSelectedObjectId(null);
  };

  // line thinkess selection
  const handleSelectLineThinknessForMeasurementObject = (thickness) => {
    // setSelectedLineThinkness(thickness);
    applyLineThinkessToSelected(thickness);
  };

  // one function: apply linestyle to the currently selected object
  const applyLineThinkessToSelected = (thickness) => {
    if (!thickness || !selectedObjectId) return;

    setSelectedLineThinkness(thickness);

    // --- 1) Update active measurements (your original flow) ---
    let nextMeasurementsSnapshot = null; // capture to build merged later
    setMeasurements((prev) => {
      const next = Array.isArray(prev)
        ? prev.map((m) =>
          m?.id === selectedObjectId ? { ...m, thickness: thickness } : m
        )
        : prev;

      nextMeasurementsSnapshot = next;

      // immediately update the canvas (so user sees it without waiting)
      try {
        measurementCanvasRef.current?.setMeasurementsOnly?.(next);
      } catch { }

      // keep your existing broadcast
      try {
        syncMeasurements?.(next);
      } catch { }

      return next;
    });

    // --- 2) If the same id exists in savedMeasurements, update there too ---
    setSavedMeasurements((prev) => {
      if (!Array.isArray(prev)) return prev;

      let found = false;
      const next = prev.map((m) => {
        if (m?.id === selectedObjectId) {
          found = true;
          return { ...m, thickness: thickness };
        }
        return m;
      });

      if (found) {
        // Build a SINGLE merged array (active wins on id conflicts)
        const byId = new Map();
        (next || []).forEach((it) => it?.id && byId.set(it.id, it)); // saved first
        ((nextMeasurementsSnapshot ?? measurements) || []).forEach(
          (it) => it?.id && byId.set(it.id, it) // then active overrides
        );
        const merged = Array.from(byId.values());

        // 🔁 Reload merged into the canvas so saved-only polygons repaint with new color
        try {
          measurementCanvasRef.current?.loadFromMergedMeasurements?.(merged);
        } catch { }

        // keep your existing saved sync
        try {
          syncSavedMeasurements?.(next);
        } catch { }
      }

      return found ? next : prev;
    });
    // setSelectedObjectId(null);
  };

  const handleUpdateSavedMeasurement = (id, patch) => {
    setSavedMeasurements((prev) =>
      prev.map((m) => (m.id === id ? { ...m, ...patch } : m))
    );
    // optionally call your snapshot saver:
    // handleSaveProjectDataApi();
  };

  return (
    <div
      className={`${image ? "main-layer" : ""
        } h-screen flex flex-col bg-[#F9FAFB]`}
      ref={screenShotRef}
    >
      {/* Header */}
      <header
        className={`bg-white border-b border-gray-200 cursor-pointer h-[66px] flex-shrink-0`}
      >
        <div className="w-full h-full flex items-center justify-between">
          <div className="flex items-center ps-2 md:ps-1 gap-3">
            <div>
              <img
                src={dentalIcons.dentalIdLogo}
                className="w-full h-[45px] object-contain hidden xl:block"
                alt="logo"
                onClick={() => navigate("/")}
              />
            </div>
            {imageSrc && (
              <div className="text-sm text-[#717784] flex flex-col items-start gap-1">
                <div className="px-0 md:px-1 py-0 bg-cyan-400/10 rounded-[32px]  inline-flex justify-center items-center gap-1 overflow-hidden">
                  <Folder
                    className="h-5 w-5 text-[#2563EB]"
                    strokeWidth={1.8}
                    color="#20C3FF"
                  />

                  <div className="text-cyan-400 text-sm font-medium">
                    #{savedProject?.identifier}
                  </div>
                </div>
                <span
                  style={{ maxWidth: "150px", maxLines: 1 }}
                  className="truncate"
                >
                  {savedProject?.title || ""}
                </span>
              </div>
            )}
            {imageSrc && (
              <div
                style={{
                  boxShadow:
                    "0 4px 6px 0 rgba(14, 18, 27, 0.06), 0 2px 4px 0 rgba(14, 18, 27, 0.03)",
                }}
                className="ms-2 px-3 flex items-center gap-2 border border-[#EDEDED] bg-white rounded-[8px] h-[45px]"
                onClick={handleUploadNewImage}
              >
                <img src={dentalIcons.uploadNewImage} alt="new" />
                <span className="text-[#20C3FF] text-[14px] font-semibold">
                  New Image
                </span>
              </div>
            )}
          </div>

          <div className="flex items-center">
            {imageSrc && (
              <BottomToolbar
                activeTool={activeTool}
                onToolChange={handleToolChange}
                zoom={zoom}
                onZoomChange={handleZoomChange}
                zoomViaSlider={zoomViaSlider}
                comments={comments}
                wrapperRotation={wrapperRotation}
                onSetRotation={handleAngleInputChange}
                startScrub={startScrub}
              />
            )}
          </div>
          {imageSrc && (
            <div className="flex items-center gap-2 px-1 justify-between pe-2">
              <div className="flex items-center gap-2 text-[12px]">
                <span className="hidden xl:block">Zoom:</span>
                <div className="h-[35px] w-[60px] hidden lg:flex items-center justify-center rounded-[8px] bg-[#F5F7FA] border border-[#EFEFEF] font-medium text-[#717784]">
                  {Math.max(100, Math.round((zoom ?? 1) * 100))}%
                </div>
              </div>
              <Tooltip title="Calculator">
                <div
                  onClick={() => setShowCalculator(!showCalculator)}
                  className={` ${showCalculator ? "bg-[#20C3FF]" : "bg-white"
                    } border border-[#EDEDED] rounded-[8px] cursor-pointer h-[35px] w-[35px] flex items-center justify-center  shrink-0`}
                  style={{
                    boxShadow:
                      "0 4px 6px 0 rgba(14, 18, 27, 0.06), 0 2px 4px 0 rgba(14, 18, 27, 0.03)",
                  }}
                >
                  <img
                    alt="calculator"
                    src={dentalIcons.calculator}
                    className={`w-6 h-6 transition"
                    ${showCalculator && "icon-white"}`}
                  />
                </div>
              </Tooltip>
              <Tooltip title="Export">
                <div
                  onClick={exportToJson}
                  className="bg-white border border-[#EDEDED] rounded-[8px] cursor-pointer h-[35px] w-[35px] flex items-center justify-center  shrink-0"
                  style={{
                    boxShadow:
                      "0 4px 6px 0 rgba(14, 18, 27, 0.06), 0 2px 4px 0 rgba(14, 18, 27, 0.03)",
                  }}
                >
                  <img
                    alt="export"
                    src={dentalIcons.exportData}
                    className={`w-6 h-6 transition"
                    `}
                  />
                </div>
              </Tooltip>
              <Tooltip title="Screenshot">
                <div
                  onClick={downloadScreenshot}
                  className="bg-white border border-[#EDEDED] rounded-[8px] cursor-pointer h-[35px] w-[35px] flex items-center justify-center  shrink-0"
                  style={{
                    boxShadow:
                      "0 4px 6px 0 rgba(14, 18, 27, 0.06), 0 2px 4px 0 rgba(14, 18, 27, 0.03)",
                  }}
                >
                  <img
                    alt="export"
                    src={dentalIcons.screenshot}
                    className="h-6 w-6"
                  />
                </div>
              </Tooltip>
              <CustomPrimaryButton
                title={!saveProjectLoading ? "Save" : "Saving..."}
                width={100}
                isRound={true}
                isHeight={true}
                onClick={handleSaveProjectDataApi}
              />
            </div>
          )}
        </div>
      </header>
      {isDataLoading ? (
        <HomePageShimer />
      ) : (
        <div
          className="w-[100%] flex h-full overflow-scroll"
          id="main-container"
        >
          {/* side bar */}
          <div className={`${image ? "w-[220px]" : "w-[0%]"}`}>
            {imageSrc && (
              <Toolbar
                startScrub={startScrub}
                handleAngleReset={handleAngleReset}
                onDirecltyRotate={handleToolClick}
                wrapperRotation={wrapperRotation}
                onSetRotation={handleAngleInputChange}
                setSelectedToolId={setSelectedToolId}
                selectedToolId={selectedToolId}
                deleteSelected={deleteSelected}
                onImageUpload={handleImageUpload}
                onReset={handleReset}
                onImageTransform={handleImageTransform}
                onMoveImage={handleMoveImage}
                onToolChange={handleToolChange}
                canUndo={canUndo}
                canRedo={canRedo}
                showCommentPanel={showCommentPanel}
                onToggleCommentPanel={handleToggleCommentPanel}
                onAdjustContrast={() => null}
                onCrop={handleCropImage}
                onOpenClick={handleOnOpenPolygon}
                StylePanel={
                  <StylePanel
                    isVisible={showStylePanel}
                    currentStyle={styleSettings}
                    onStyleChange={handleSelectColorForMeasurementObject}
                    position={stylePanelPosition}
                    onLineStyleChange={
                      handleSelectLineStyleForMeasurementObject
                    }
                    onThinknessChange={
                      handleSelectLineThinknessForMeasurementObject
                    }
                    selectedLineThikness={selectedLineThikness}
                    selectedLineStyle={selectedLineStyle}
                  />
                }
                scale={scale}
                onScaleChange={(s) => {
                  setScale(s);
                  syncScale({ scale: s });
                }}
                onUnitChange={(u) => {
                  setUnit(u);
                  syncScale({ unit: u });
                }}
                handleCropImage={handleCropImage}
                comments={comments}
                onHorizontalFlip={handleFlipImage}
              />
            )}
          </div>
          {/* main content  */}
          <div
            ref={zoomShellRef}
            className={`relative ${image ? "w-[68%]" : "w-[100%]"
              } h-min-screen overflow-hidden `}
          >
            <ZoomOnly
              value={zoom} // controlled
              onChange={setZoom}
              min={1} // never below 100%
              max={8}
              className="w-full h-full" // <— center zoom
            >
              <div id="zoom-root" className="flex-1 overflow-auto relative">
                {!imageSrc ? (
                  <div className="flex-1 flex w-full items-center justify-center p-8 h-full">
                    <div className="w-full max-w-[850px]  space-y-6">
                      <div className="bg-transparent">
                        <div className="p-0">
                          <div className="text-center mb-4">
                            <h3 className="font-semibold text-gray-900 mb-2">
                              Measurement Tools
                            </h3>
                            <p className="text-sm text-gray-600">
                              Precise analysis tools for dental imaging
                            </p>
                          </div>

                          <div className="grid grid-cols-4 gap-2 ">
                            <div className="text-center group bg-white p-4 rounded-[12px]">
                              <div className="w-14 h-14 mx-auto mb-2 bg-blue-50 rounded-xl flex items-center justify-center group-hover:bg-blue-100 transition-colors duration-200">
                                <img src={dentalIcons.leanerIcon} />
                              </div>
                              <strong className="font-semibold text-[#616161] mb-2">
                                Linear Measurement
                              </strong>
                            </div>

                            <div className="text-center group bg-white p-4 rounded-[12px]">
                              <div className="w-14 h-14 mx-auto mb-2 bg-blue-50 rounded-xl flex items-center justify-center group-hover:bg-blue-100 transition-colors duration-200">
                                <img src={dentalIcons.angleIcon} />
                              </div>
                              <strong className="font-semibold text-[#616161] mb-2">
                                Polygon Measurement
                              </strong>
                            </div>

                            <div className="text-center group bg-white p-2 rounded-[12px] flex flex-col items-center justify-center">
                              <div className="w-14 h-14 mx-auto mb-2 bg-purple-50 rounded-xl flex items-center justify-center group-hover:bg-purple-100 transition-colors duration-200">
                                <img src={dentalIcons.areaIcon} />
                              </div>
                              <strong className="font-semibold text-[#616161] mb-0">
                                Angle Measurement
                              </strong>
                            </div>

                            <div className="text-center group bg-white p-2 rounded-[12px] flex flex-col items-center justify-center">
                              <div className="w-14 h-14 mx-auto mb-2 bg-purple-50 rounded-xl flex items-center justify-center group-hover:bg-purple-100 transition-colors duration-200">
                                <img src={dentalIcons.secondOpicon} />
                              </div>
                              <strong className="font-semibold text-[#616161] mb-0">
                                Second Opinion
                              </strong>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        className="bg-white rounded-[12px]"
                      >
                        <CardContent className="p-8 text-center">
                          <div className="w-[100px] h-[100px] mx-auto mb-6 bg-blue-50 rounded-[12px] flex items-center justify-center">
                            <img src={dentalIcons.uploadImageIcon} />
                          </div>

                          <h4 className="text-xl font-semibold text-gray-900 mb-2">
                            Upload Dental Image
                          </h4>
                          <p className="text-gray-600 mb-8">
                            Drag and drop your image here, or click to browse
                          </p>

                          <div className="relative">
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleImageUpload(file);
                              }}
                              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                            />

                            <Button
                              size="lg"
                              className="bg-[#20C3FF] text-white px-8 py-3"
                            >
                              Choose Image
                            </Button>
                          </div>
                        </CardContent>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <MeasurementCanvas
                      thumbnailReff={thumbnailReff}
                      ref={measurementCanvasRef}
                      saveCanvasReferenceForOtherComponents={
                        saveCanvasReferenceForOtherComponents
                      }
                      onScaleOverlayChange={onScaleOverlayChange}
                      selectedObjectId={selectedObjectId}
                      setSelectedObjectId={setSelectedObjectId}
                      onSetScaleLength={(value) => {
                        setScaleLength(value);
                        syncScale({ scaleLength: value });
                      }}
                      // syncScale={syncScale}
                      canvasSize={canvasSize}
                      onCanvasSizeChange={setCanvasSize}
                      displaySize={displaySize}
                      onDisplaySizeChange={setDisplaySize}
                      activeTool={activeTool}
                      setActiveTool={setActiveTool}
                      image={image}
                      scale={scale}
                      unit={unit}
                      onMeasurementAdd={handleMeasurementAdd}
                      onPolygonEditStart={handlePolygonEditStart}
                      imageTransforms={imageTransforms}
                      onImageTransformChange={setImageTransforms}
                      onHistoryChange={handleHistoryChange}
                      onScreenshotRequest={screenshotRequested}
                      styleSettings={styleSettings}
                      isPanMode={isPanMode}
                      onAddComment={handleAddComment}
                      comments={comments}
                      selectedCommentId={selectedCommentId}
                      onSelectComment={setSelectedCommentId}
                      onShowCommentInput={handleShowCommentInput}
                      onShowCommentTooltip={handleShowCommentTooltip}
                      onShowCommentThread={handleShowCommentThread}
                      zoom={zoom}
                      onZoomChange={handleZoomChange}
                      updateMeasurementRunTime={updateMeasurementRunTime}
                      wrapperRotation={wrapperRotation}
                      colorUpdated={selectedColor}
                      lineStyleUpdated={selectedLineStyle}
                      lineThicknessUpdated={selectedLineThikness}
                    />
                  </>
                )}
              </div>
            </ZoomOnly>

            {isCropping && (
              <div className="fixed top-0 left-0 z-[999] w-full min-h-full bg-black bg-opacity-50 flex items-center justify-center">
                <div className="bg-white p-4 rounded-md shadow-md max-w-3xl max-h-[90vh] overflow-y-auto">
                  <ReactCrop
                    maxHeight={"100%"}
                    maxWidth={"100%"}
                    crop={crop}
                    onChange={(c) => setCrop(c)}
                    onComplete={(c) => setCompletedCrop(c)}
                  >
                    <img
                      ref={imgRef}
                      src={imageSrc}
                      alt="To be cropped"
                      style={{
                        objectFit: "contain",
                        maxHeight: "600px",
                      }}
                    />
                  </ReactCrop>
                  <div className="flex justify-end mt-4">
                    <button
                      onClick={() => {
                        setIsCropping(false);
                        setCrop({
                          unit: "%", // or "px"
                          x: 10,
                          y: 10,
                          width: 50, // 50% of the image width
                          height: 50,
                        });
                      }}
                      className="mr-2 bg-gray-200 hover:bg-gray-300 py-2 px-4 rounded-md"
                    >
                      Cancel
                    </button>
                    {/* <button
                      onClick={handleSkipCrop}
                      className="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded-md mx-3"
                    >
                      Skip
                    </button> */}
                    <button
                      onClick={handleCropDone}
                      className="bg-blue-500 hover:bg-blue-700 text-white py-2 px-4 rounded-md"
                    >
                      Crop
                    </button>
                  </div>
                </div>
              </div>
            )}

            <CommentInput
              position={commentInputPosition}
              onSubmit={(text) => {
                const canvasPos = pageToCanvas(commentInputPosition); // ← create it here

                handleAddComment({
                  position: canvasPos,
                  text,
                  isResolved: false,
                });
                setShowCommentInput(false);
              }}
              onCancel={() => {
                setActiveTool("select");
                setShowCommentInput(false);
              }}
              isVisible={showCommentInput}
            />

            <CommentTooltip
              comment={tooltipComment}
              position={tooltipPosition}
              isVisible={showCommentTooltip && tooltipComment !== null}
            />

            <CommentThread
              comment={threadComment}
              position={threadPosition}
              onClose={() => setShowCommentThread(false)}
              onResolve={handleResolveComment}
              onDelete={handleDeleteComment}
              onEditComment={handleEditComment}
              onAddReply={handleAddReply}
              isVisible={showCommentThread && threadComment !== null}
            />
            {/* {imageSrc && (
              <BottomToolbar
                activeTool={activeTool}
                onToolChange={handleToolChange}
                zoom={zoom}
                onZoomChange={handleZoomChange}
                zoomViaSlider={zoomViaSlider}
                comments={comments}
              />
            )} */}
            <RotationWarning
              isVisible={rotationWarning}
              onCancel={() => {
                setRotationWarning(false);
                pendingAngleRef.current = null;
              }}
              onYes={() => onTransFormHandel(pendingAngleRef?.current)}
            />
            <ResetWarning
              isVisible={resetWarning}
              onCancel={() => {
                setResetWarning(false);
                pendingAngleRef.current = null;
              }}
              onYes={() => {
                setResetWarning(false);
                onTransFormHandel(0);
                handleReset();
              }}
            />
            <CropWarning
              isVisible={cropWarning}
              onCancel={() => {
                setCropWarning(false);
              }}
              onYes={() => {
                setCropWarning(false);
                handleReset();
                setIsCropping(true);
              }}
            />
            <FlipImageWarming
              isVisible={flipWarning}
              onCancel={() => {
                setFlipWarning(false);
              }}
              onYes={() => {
                setFlipWarning(false);
                handleReset();
                handleFlipHorizontal();
              }}
            />
          </div>
          {/* measurement panel */}
          <div
            className={`${image ? "w-[300px] xl:w-[340px] " : "w-[0%]"} h-full`}
          >
            {image && (
              <div className="flex-shrink-0  h-[90vh] overflow-auto m-2 rounded-[16px] border border-[#F4F4F4]">
                <MeasurementPanel
                  onTabChange={setSelectedTab}
                  syncScale={syncScale}
                  projectId={projectId}
                  onClearCamparedResult={onClearCamparedResult}
                  resetImportData={resetImportData}
                  onHighlightPolygon={handleHighlightPolygon}
                  showCalculator={showCalculator}
                  handleCropImage={handleCropImage}
                  showCommentPanel={showCommentPanel}
                  CommentPanel={
                    <CommentPanel
                      setShowCommentPanel={() => {
                        setShowCommentPanel(!showCommentPanel);
                        handleToolChange("select");
                      }}
                      comments={comments}
                      onAddComment={handleAddComment}
                      onEditComment={handleEditComment}
                      onDeleteComment={handleDeleteComment}
                      onResolveComment={handleResolveComment}
                      selectedCommentId={selectedCommentId}
                      onSelectComment={setSelectedCommentId}
                      handleAddReply={handleAddReply}
                    />
                  }
                  resetImageProps={resetImageProps}
                  toggleInvert={toggleInvert} // like this
                  toggleSharpen={toggleSharpen} //like this
                  imageControls={imageControls}
                  onContrastChange={setCanvasContrast}
                  onBrightnessChange={setCanvasBrightness}
                  onZoomChange={setCanvasZoom}
                  curves={curves}
                  onCurvesShadowsChange={handleCurvesShadows}
                  onCurvesMidtonesChange={handleCurvesMidtones}
                  onCurvesHighlightsChange={handleCurvesHighlights}
                  onCurvesReset={resetCurves}
                  onSharpenAmountChange={onSharpenAmountChange}
                  onSharpenRadiusChange={onSharpenRadiusChange}
                  onReduceNoiseChange={onSharpenNoiseChange}
                  sharpenAmount={sharpenAmount}
                  sharpenNoise={sharpenNoise}
                  sharpenRadius={sharpenRadius}
                  resetSmartSharpen={resetSmartSharpen}
                  setShowCalculator={() => setShowCalculator(!showCalculator)}
                  scale={scale}
                  measurements={measurements}
                  savedMeasurements={savedMeasurements}
                  onSaveMeasurement={handleSaveMeasurement}
                  onDeleteSavedMeasurement={handleDeleteSavedMeasurement}
                  onRemoveActiveMeasurement={handleRemoveActiveMeasurement}
                  // Levels Adjustment
                  levelsAdjustments={levelsAdjustments}
                  onLevelAdjustment={handleLevelAdjustment}
                  onLevelsReset={resetLevelsAdjustments}
                  levelsHistogram={levelsHistogram}
                  onAppendPolygon={onAppendPolygon}
                  onUpdateSavedMeasurement={handleUpdateSavedMeasurement}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DentalMeasurementTool;
